// Briefing index + viewer rendering. Pure DOM; no framework.
//
// The viewer mirrors the synthesizer payload shape (threads + unthreaded +
// needs_review). Each renderable line gets a `<form>` with a `data-target-id`
// composite path key — see target_id.ts for the contract. Form submission
// is wired up in forms.ts.

import {
  apiPathPrefix, deleteJson, getJson, inPreviewMode, postEmpty, postJson,
} from "./api";
import {
  acceptProposedAnswer,
  dismissProposedAnswer,
  mergeProposalDismissed,
  proposedAnswerDismissed,
  renderFormControls,
  type ActionOption,
  type ClosedActionOption,
  type ItemOption,
  type ParticipantOption,
  type SubcategoryOption,
  type ThreadOption,
} from "./forms";
// #60 File-under-Closed: the closed-Item picker unions the briefing blob
// (≤2-week retention) with the all-ages Closed-Items snapshot. Fixture mode
// uses the stubbed snapshot (mirrors app.ts's fetchClosed binding; fixture's
// imports of this module are type-only, so no runtime cycle).
import { fetchClosedItems, type ClosedItemsSnapshot } from "./closed_items";
import { fetchFixtureClosedItems, inFixtureMode } from "./fixture";

// Memoized per session — the viewer re-renders on every KIL toggle /
// feedback-state change, and re-firing GET /closed-items each time is
// wasted load (the snapshot moves at most hourly). A failed fetch clears
// the memo so the next render retries.
let closedItemsSnapshotMemo: Promise<ClosedItemsSnapshot> | null = null;
function closedItemsSnapshotOnce(): Promise<ClosedItemsSnapshot> {
  if (!closedItemsSnapshotMemo) {
    closedItemsSnapshotMemo = (
      inFixtureMode() ? fetchFixtureClosedItems() : fetchClosedItems()
    ).catch((err) => {
      closedItemsSnapshotMemo = null;
      throw err;
    });
  }
  return closedItemsSnapshotMemo;
}

// Decision 5.4 bulk-mark shortcut ("Mark all (N) for PSU" on the
// Actions section header). Hidden by default per operator request —
// the render is distracting before clients have asked for it. Flip
// to true to restore. The implementation and CSS stay intact.
const BULK_KIL_MARK_VISIBLE = false;

// Category-level one_liner / description rendering. The client spec
// (tmp/scale-brief-structure.pdf) defines a Category as just a
// tag/number + Open/Closed status — no description requested. The
// previous render leaned on `thread.one_liner` as an auto-summary of
// items below, which drifted from the spec. Synth rule 12 was also
// updated to stop emitting one_liner; this renderer flag is the
// belt-and-suspenders gate for historical payloads that still carry
// one_liner data. Flip to true if the client later asks for
// Category descriptions to come back.
const CATEGORY_DESCRIPTION_VISIBLE = false;
import {
  actionItemKey,
  needsReviewItemKey,
  subcategoryKey,
  threadItemKey,
  threadKey,
  unfiledKey,
  informationalKey,
  unthreadedItemKey,
  unthreadedWorkItemKey,
  unthreadedWorkItemSectionItemKey,
  workItemKey,
  workItemSectionItemKey,
  type Section,
} from "./target_id";

export interface BriefingListItem {
  run_id: string;
  week_start: string;
  week_end: string;
  stored_at: string;
}

export interface BriefingPayload {
  project: {
    slug: string;
    name?: string;
    display_name?: string;
    // Slice 8 Item #5 follow-up: roster seeds for collectParticipantOptions.
    // team_members is the configured project roster (project YAML).
    // participants is the observed-in-traffic roster from state.db. Both
    // are injected by the agent harness; either can be omitted when
    // empty. The SPA merges these with names parsed out of the briefing
    // body to produce the responsible_party_assign datalist.
    team_members?: Array<{ name: string; email?: string | null; role?: string | null }>;
    participants?: Array<{ name: string; email?: string | null }>;
  };
  week: { start: string; end: string };
  // #66 D4: proposed duplicates, rendered in Needs Your Input rather than
  // inline. DECLARED rather than read through a cast — a cast compiles but
  // leaves the field invisible to every other consumer, including the fixture,
  // which is how this was first written.
  merge_proposals?: MergeProposalView[];
  // Slice 7: present (=2) on payloads emitted by the v2 synthesizer.
  // Absent on legacy payloads. The viewer uses this to route between
  // renderers. v4 (Category → Item → Action redesign) bumps this to 4.
  payload_schema_version?: number;
  // Slice 7 feedback item #6: when the agent attached the rendered
  // .docx, this is its base64-encoded body. The SPA never decodes it
  // client-side — the download button hits the Worker's
  // GET /briefings/:runId/download route which streams decoded bytes.
  docx_b64?: string;
  executive_summary?: string;
  threads?: ThreadView[];
  unthreaded?:
    | (Partial<Record<Section, AnyItem[]>> & { work_items?: WorkItemView[] })
    | undefined;
  // v4 (payload_schema_version === 4): top-level orphan bucket for
  // items the synthesizer couldn't confidently place under any
  // Category → Item. Absent on v3 payloads.
  unfiled?: UnfiledItemView[];
  // A² (2026-06-03): Needs-Your-Input FYI bucket. Low-confidence /
  // pure-FYI content the synth surfaced here instead of fabricating a
  // thin Item + Informational Action in the Category tree. Absent on
  // pre-A² payloads.
  informational?: InformationalItemView[];
  // A³/A² (2026-06-03): entries the recipient dismissed-as-acknowledged —
  // rendered as a collapsed "Acknowledged this period (N)" line.
  informational_acknowledged?: InformationalItemView[];
  candidate_threads?: CandidateThreadView[];
  needs_review?: Array<{ item_id: string; summary: string; why_flagged?: string }>;
  nothing_new?: boolean;
  // Past corrections raised against this run_id, threaded through by
  // agent/portal.py::_briefing_envelope. Used to render "corrected"
  // badges on matching rows. Items with no `target.id` are present in
  // the array but not rendered (their effect is already visible through
  // the synthesizer's output).
  corrections?: Correction[];
}

export interface Correction {
  kind: string;
  recorded_at?: string;
  recorded_by?: string;
  applies_to_run_id?: string | null;
  target?: { type?: string; id?: string };
  // ClassificationCorrection / DateCorrection / ThreadTagCorrection
  item_id?: string;
  // DateCorrection
  field?: string;
  new_value?: string;
  // OperatorNote
  kind_note?: string;
  text?: string;
  // ThreadRenameCorrection / ThreadMergeCorrection / ThreadTagCorrection
  from_slug?: string;
  to_slug?: string;
  to_name?: string;
  source_slugs?: string[];
  target_slug?: string;
  from_thread?: string;
  to_thread?: string;
}

// #38: an unnumbered, recipient-created grouping inside a Category (the overlay
// tier). Stamped into the blob from state every cron; never synth-emitted.
// Sorted (created_at ASC, seq tiebreak) by the renderer; a closed group renders
// as a header-only tombstone for 14 days, then the stamp stops emitting it.
export interface SubCategoryView {
  slug: string;
  name: string;
  status?: "open" | "closed" | string;
  created_at?: string | null;
  closed_at?: string | null;
  closed_reason?: string | null;
  // Hidden backend ordinal (MAX+1 per Category) — render tiebreak only, never
  // shown (the "no Dewey decimal" guarantee).
  seq?: number | null;
}

// #37 phase tier. Closed 4-value enum (mirrors the Pydantic Literal in
// agent/payload_v4.py and the Zod enum in worker/src/schemas.ts) — the
// code-level enum structurally enforces "no new phases mid-project".
// Underscore-canonical storage; PHASE_LABELS maps to the display banners.
export type PhaseValue =
  | "pre_development"
  | "pre_construction"
  | "construction"
  | "general";

// Fixed render order, top → bottom. General is last so the Needs-Your-Input
// tail (which lives in the General band) sits where today's global tail does.
export const PHASE_ORDER: readonly PhaseValue[] = [
  "pre_development",
  "pre_construction",
  "construction",
  "general",
];

export const PHASE_LABELS: Record<PhaseValue, string> = {
  pre_development: "Pre-Development",
  pre_construction: "Pre-Construction",
  construction: "Construction",
  general: "General",
};

// A thread's phase coerced to a render band: null/absent/unknown => General.
export function phaseBand(phase?: string | null): PhaseValue {
  return phase && (PHASE_ORDER as readonly string[]).includes(phase)
    ? (phase as PhaseValue)
    : "general";
}

export interface ThreadView {
  slug: string;
  name: string;
  status?: string;
  inferred?: boolean;
  // #38: sub-category grouping overlay — flat sibling metadata. Items keep
  // `work_items[]` as their only child array; the renderer partitions that
  // flat list by `work_items[].subcategory_slug` at render time.
  subcategories?: SubCategoryView[];
  // #37: phase placement overlay — a state-owned attribute (config-seeded,
  // recipient-migratable via category_move_to_phase), stamped from state by
  // the harness. null/absent => unphased/legacy, rendered in the General band.
  phase?: PhaseValue | null;
  one_liner?: string;
  first_seen?: string;
  // v4: client-facing path number (e.g. "1") rendered by the v4 SPA
  // path. Absent on v3 payloads; v4 renderer falls back gracefully.
  display_number?: string;
  // v4: closure timestamp; populated when status === "closed".
  closed_at?: string;
  // v4: italic inline annotation when this Category was recently
  // moved (Decision 3.6). Renders for 2 weeks.
  breadcrumb?: BreadcrumbView | null;
  // Legacy: categories sit directly on the thread.
  completed?: AnyItem[];
  milestones?: AnyItem[];
  in_progress?: AnyItem[];
  decisions?: AnyItem[];
  blockers?: AnyItem[];
  action_items?: AnyItem[];
  open_questions?: AnyItem[];
  leadership_attention?: AnyItem[];
  // Slice 7: categories nest inside work_items.
  work_items?: WorkItemView[];
  archived_work_items?: ArchivedWorkItemView[];
}

export interface WorkItemView {
  slug: string;
  name: string;
  summary?: string;
  status?: "open" | "closed" | string;
  first_seen?: string;
  involved?: string[];
  key_dates?: string[];
  // Slice 8 Item #5 (v3): per-work-item responsible_party. Lifecycle
  // is register-once on the agent side — operator_override always wins,
  // then prior registration, then computed, then default_pm.
  responsible_party?: ResponsiblePartyView | null;
  // Slice 8 Item #3 (v3): compactness hint. true → routine sections
  // collapse into `pending`; always-full surfaces still render.
  compact?: boolean;
  pending?: string[];
  // v4 (payload_schema_version === 4) additions per Section 8.3 of the
  // Category → Item → Action redesign.
  // - `serial` / `display_number`: stable backend ID (I-NNN) + path
  //   number ("1.1"). Both absent on v3 payloads.
  serial?: number;
  display_number?: string;
  // - `narrative`: 2-3 line description rendered under the Item title
  //   (Decision 1.2). v3 used `summary` for this role; v4 splits them
  //   — `summary` is the short status line, `narrative` is the body.
  narrative?: string | null;
  // - `start_date`: alias for `first_seen` in v4 vocabulary. Renderer
  //   should accept either.
  start_date?: string;
  // #38: grouping pointer into the parent Category's `subcategories[]`
  // (null/absent = ungrouped, today's layout). Stamped from state post-upsert;
  // the deterministic-only class display_number/serial occupy. Never synth-
  // emitted. display_number is untouched by a grouping move.
  subcategory_slug?: string | null;
  // - `closed_at`: populated when status === "closed".
  closed_at?: string;
  // A①: closure reason — "merged into <target>" routes the Item to the
  // "Recently Merged" section. Set optimistically on merge submit.
  closed_reason?: string | null;
  // - `kil_marked`: persistent Project Status Update mark (Section 5). Marks
  //   are sticky across weeks until unmarked or aged out.
  kil_marked?: boolean;
  // - `breadcrumb`: italic inline annotation when this Item was
  //   recently moved between Categories (Decision 3.6).
  breadcrumb?: BreadcrumbView | null;
  // - `matter_note`: #66 consult annotation. `absorbed` = a re-minted
  //   recurrence of this Item's matter was folded into it this run (a
  //   breadcrumb). `ambiguous` = this Item may belong to a known matter —
  //   surfaced inline with a "place it" nudge to the existing Merge
  //   affordance (`targets` = the candidate member slugs). Render-only,
  //   re-derived every run by the consult (never persisted).
  matter_note?: MatterNoteView | null;
  // - `merge_candidate`: #66 D1-D3 dedup-consult overlap proposal (surface-
  //   only, re-derived every run). Renders an inline ◇ note + feeds the ✎
  //   menu's orange merge banner when a counterpart Item resolves.
  merge_candidate?: MergeCandidateView | null;
  completed?: AnyItem[];
  milestones?: AnyItem[];
  in_progress?: AnyItem[];
  decisions?: AnyItem[];
  blockers?: AnyItem[];
  action_items?: AnyItem[];
  open_questions?: AnyItem[];
  leadership_attention?: AnyItem[];
  // BACKLOG #28: per-Work-Item closed AIs. Rendered as a compact
  // "Closed action items (N)" sub-section under the WI card.
  archived_action_items?: ArchivedActionItemView[];
  // A² (2026-06-03): true when this Item first appears this period
  // (set deterministically by the agent's mark_new pass). Drives the
  // NEW pill + "New only" filter.
  is_new?: boolean;
  // A1 (2026-07-22): true when the narrative-merger judged this carryover
  // Item a MERGE this period (real new info). Drives the "Updated" pill +
  // "new/updated" filter. Set by merge_narratives; rides the blob like is_new.
  is_updated?: boolean;
}

export interface ResponsiblePartyView {
  name: string;
  email?: string | null;
  source?: "operator_override" | "synthesized" | "default_pm" | string;
  locked_at?: string;
}

export interface CandidateThreadView {
  slug: string;
  name: string;
  one_liner?: string;
  first_seen?: string;
  // NEW-this-period badge (mark_new): true when the suggested Category was
  // first proposed this run (first_seen in the week window).
  is_new?: boolean;
  // Slice 8 Item #1 Layer B: structural (promote/merge decision) vs
  // relevance_uncertain (confirm-belonging decision). Absent on v3
  // payloads emitted before the agent-side bucketing landed; defaults
  // to "structural" in the renderer.
  bucket?: "structural" | "relevance_uncertain";
}

export interface ArchivedWorkItemView {
  slug: string;
  name: string;
  closed_at?: string;
  closed_reason?: string;
}

// BACKLOG #28: closed AIs render in a per-work-item "Closed action
// items" sub-section. The serial is preserved through close so the
// reader sees what came off the plate this week.
export interface ArchivedActionItemView {
  ai_slug: string;
  serial: number;
  text: string;
  closed_at?: string;
  closed_reason?: string;
}

export interface RecommendedNextStep {
  text: string;
  // Machine-readable item id (hash). Preserved for future SPA features
  // that navigate back to the source item.
  source_ref?: string;
  observed_at?: string;
  // Human-readable label resolved by the agent harness from the source
  // item ("Ray Lastra email, 5/4"). When present, the renderer uses
  // this in place of the hash. Absent on briefings rendered before
  // the source-label feature landed; the renderer falls back to
  // source_ref then.
  attribution?: string;
}

interface AnyItem {
  what?: string;
  // v4 payload key for Action title. Pydantic ActionItem uses
  // `text: str = Field(..., alias="what")` — serializes as `text`,
  // accepts `what` as input alias. SPA must read `text` first to
  // pick up the actual payload field; `what` retained as fallback
  // for any pre-v4 briefings that still hold the old key.
  text?: string;
  question?: string;
  // Slice 7 L3 feedback: every category entry can carry a 1-3 sentence
  // verbose description below the short `what`/`question` lead.
  // Synthesizer is prompted to split substance into a scannable bold
  // title (`what`) plus this verbose body. Optional; missing fall back
  // to title-only rendering.
  description?: string | null;
  owner?: string | null;
  assignee?: string | null;
  status?: string;
  decided_by?: string | null;
  decided_at?: string | null;
  due?: string | null;
  raised_by?: string | null;
  why?: string;
  // Slice 8 Item #6 (v3): non-null on impact-bearing action_items (the
  // formerly-blocker class). Pairs with `stale` to gate the Risk badge
  // — impact != null AND stale == false renders [Risk] + always-full.
  impact?: string | null;
  // Slice 7: object with text/source/date. Legacy briefings may still
  // carry a plain string; both shapes accepted, normalized at render.
  recommended_next_step?: RecommendedNextStep | string | null;
  significance?: string;
  notes?: string;
  attribution?: string;
  recommend_action?: string;
  // Slice 8 Item #6 (v3): unified carryover flag, replaces pre-v3
  // `persistent` for blocker-shaped items.
  stale?: boolean;
  first_seen?: string;
  // BACKLOG #28 (v3+): per-Work-Item stable AI handle. `ai_slug` is
  // the cross-week identity; `serial` is the renderer-friendly
  // integer (displayed as #NNN). Both are written by the pipeline's
  // action_items upsert; legacy or un-numbered AIs have neither and
  // render without the prefix.
  ai_slug?: string;
  serial?: number;
  // #66 D1-D3 (2026-07-23): dedup-consult overlap proposal on an Action —
  // inline ◇ advisory only (consolidation runs through the existing ✎ menu
  // "Merge into…" / "Move to another Item" affordances; no one-click in v1).
  merge_candidate?: MergeCandidateView | null;
  // v4 (payload_schema_version === 4) additions on action_item rows.
  // Other section kinds (completed, decisions, etc.) don't populate
  // these; they're declared here because AnyItem is the shared shape
  // across all section arrays.
  display_number?: string;        // path number, e.g. "1.1.2"
  narrative?: string | null;       // 1-2 line description (Decision 7.3)
  priority_status?: PriorityStatus;    // 4-state enum (Decision 1.3)
  action_type?: ActionType;            // "regular" | "invoices_aggregate"
  kil_marked?: boolean;                // Project Status Update mark
  reason?: string | null;              // Critical / Delay / On Hold metadata
  off_hold_condition?: string | null;  // On Hold metadata (Decision 4.2)
  // v4 puts a `responsible_party` directly on action rows so the
  // Action meta line can render "Responsible: NAME" (Decision 2.7
  // + 4.4). v3 used the work-item-level responsible_party only.
  responsible_party?: ResponsiblePartyView | null;
  open_questions_v4?: OpenQuestionView[];
  // ^ named `_v4` to avoid collision with the v2/v3 `open_questions`
  // category section under work_items, which is `AnyItem[]` (the
  // string-question shape). v4 puts open questions on Actions; the
  // SPA pivots to this field when payload_schema_version === 4.
  invoices?: InvoiceView[];            // billing aggregate (Section 11)
  breadcrumb?: BreadcrumbView | null;
  // Option Y: a live "propose closing" candidate on an open Action/Item.
  // Presence drives the *_dismiss_closure_candidate ("Keep open") affordance
  // (read via cast at the menu call sites — declared here so fixtures and
  // callers share one shape).
  closure_candidate?: { reason?: string } | null;
  closed_at?: string;
  // A1: closure reason — "merged into <target>" routes the Action to the
  // "Recently Merged" section. Set optimistically on merge submit.
  closed_reason?: string | null;
  // A2 (2026-06-03): true when this Action first appears this period
  // (agent mark_new pass). Drives the NEW pill + "New only" filter.
  is_new?: boolean;
  // A1 (2026-07-22): true when the narrative-merger judged this carryover
  // Action a MERGE this period (real new info). Drives the "Updated" pill +
  // "new/updated" filter. Set by merge_narratives; rides the blob like is_new.
  is_updated?: boolean;
}

// --- v4 (Category → Item → Action redesign) ---------------------------------

// 4-state priority on Actions, orthogonal to Open/Closed lifecycle
// (Decision 1.3). Drives the action-row left-edge color + status pill.
export type PriorityStatus = "Critical" | "Delay Potential" | "Active" | "On Hold" | "Informational";

// Action variant discriminator (Decision 11.1). `invoices_aggregate`
// is the per-Item "Invoices & Pay Applications" rollup with a nested
// `invoices[]` array; `regular` is everything else.
export type ActionType = "regular" | "invoices_aggregate";

export interface BreadcrumbView {
  /** #90 step 10 — "recategorized" (a real move) vs "renumbered" (an
   * ancestor's number changed; this entity did not change parent). Optional:
   * blobs written before step 10 carry no `kind` and fall back. */
  kind?: string;
  // Client-visible path notation the entity carried *before* the
  // move (e.g. "1.4"). Renders italic inline in the entity title.
  former_path: string;
  // ISO8601 calendar date of the move. Drives the 2-week
  // retention window (Decision 3.6) — after that, breadcrumb drops.
  moved_at: string;
}

/** The "(formerly N.M, recategorized DATE)" note, as ONE renderer.
 *
 * Extracted 2026-08-11. The 2026-08-10 ruling is that the briefing and the
 * state-derived snapshots must not say different things about a renumbered
 * entity. The agent already guarantees the DATA agrees by reading both from
 * `display_number_breadcrumbs`; this is the render half of the same guarantee
 * — Closed Items and the KIL/PSU view call this exact function rather than
 * re-implementing the wording, so the two surfaces cannot drift in phrasing
 * the way they previously drifted in content.
 *
 * `kind` falls back to "recategorized" for blobs written before step 10.
 */
export function renderBreadcrumb(bc: BreadcrumbView): HTMLSpanElement {
  const el = document.createElement("span");
  el.className = "item-breadcrumb";
  el.textContent =
    `(formerly ${bc.former_path}, ${bc.kind ?? "recategorized"} ${bc.moved_at})`;
  return el;
}

// #66 PR3: the matter-ledger consult's inline annotation on an Item. The
// consult is SURFACE-ONLY, so the only kind is `ambiguous` — this Item may
// belong to a known matter/member; rendered inline with a nudge to place it
// via the existing Merge-Into-Existing affordance. `targets` are the candidate
// member work_item slugs (resolved to item numbers + names at render time).
export interface MatterNoteView {
  kind: "ambiguous";
  text: string;
  matter_ids?: string[];
  targets?: string[];
}

/** #66 D1-D3 (2026-07-23): structured locator for one counterpart node in a
 * merge_candidate — aligned index-for-index with `targets` labels. An Action
 * target's mergeable Item is its PARENT (`item`); an Item target is itself.
 * Slugs are the wire identity — never key an accept on display numbers. */
export interface MergeTargetView {
  thread: string;
  item: string;
  action?: string | null;
  label?: string;
}

/** #66 D1-D3: the dedup consult's SURFACE-ONLY cross-source overlap proposal
 * (the synth's own "overlaps with…" tell, re-derived every run). `targets` are
 * readable labels; `target_locators` (2026-07-23+) the machine twin — absent
 * on older blobs, so labels-only is the primary degrade path until the first
 * post-promote cron. */
/** #66 D4 — a proposed duplicate, surfaced as a NEEDS-YOUR-INPUT row rather
 *  than inline. Measured precision ~27% and the judge's self-reported
 *  confidence does not separate, so there is no basis for putting these
 *  against the recipient's briefing rows. */
export interface MergeProposalSideView {
  thread: string;
  slug: string;
  name?: string;
  display_number?: string | null;
  category?: string | null;
  narrative?: string | null;
  action_count?: number;
}
export interface MergeProposalView {
  id: string;
  fold: MergeProposalSideView;
  into: MergeProposalSideView;
  rationale?: string;
  source?: string;
  confidence?: string;
}

export interface MergeCandidateView {
  kind: "overlap";
  text: string;
  targets?: string[];
  target_locators?: MergeTargetView[];
  evidence?: string[];
}

export interface OpenQuestionView {
  text: string;
  // #52b: stable identity (state-owned; survives a synth reword). Absent on
  // pre-#52b blobs — the picker falls back to keying by text.
  qid?: string | null;
  raised_by?: string | null;
  raised_at?: string | null;
  // Populated when an operator marks the question answered. The
  // Action keeps its priority status throughout — Open Question is
  // a *field*, not a 5th priority (Decision 1.4).
  answer?: string | null;
  answered_at?: string | null;
  // N2/#117 — a confident, source-grounded proposed answer stamped by the
  // answer-proposer pass (present only while unanswered). The recipient
  // one-click-accepts it, which submits action_open_question_resolve{qid, answer}.
  resolution_candidate?: {
    answer: string;
    source_ref?: string | null;
    proposed_at?: string | null;
    source_subject?: string | null;
    source_author?: string | null;
    source_timestamp?: string | null;
  } | null;
}

export interface InvoiceView {
  number: string;
  received_date?: string | null;
  amount?: number | null;
  status?: "Received" | "Under Review" | "Approved" | "Paid" | "Disputed";
  due_date?: string | null;
  blocking_note?: string | null;
  source_ref?: string | null;
}

// v4 placement-orphan items the synthesizer couldn't tentatively route
// to a Category → Item (Decision 1.5). Renders in a top-level panel
// above the Category tree. Operator triages each via the pencil
// control (place in existing Item / create new Item / not relevant /
// flag next week / comment).
export interface UnfiledItemView {
  id: string;
  text: string;
  narrative?: string | null;
  source_ref: string;
  source_author?: string | null;
  source_timestamp?: string | null;
  attribution?: string | null;
  // Optional placement hint — populated when the synthesizer
  // detected that the item topic-matches a closed Item (Decision 4.8).
  hint?: string | null;
  // #61 (2026-06-09): "you're missing information" framing — what's absent
  // to place this as an Item/Action, plus the synth's best tier guess.
  // Absent on historical blobs (additive payload fields).
  missing?: string[] | null;
  suggested_tier?: "item" | "action" | null;
  // #60 PR4-v2 (#1.4): set on rows folded in from a candidate thread — the SPA
  // threads it through Place/Merge so the absorber retires the inferred thread.
  candidate_slug?: string | null;
  // A² (2026-06-03): true when this orphan first appears this period.
  is_new?: boolean;
}

// A² (2026-06-03): one entry in the Needs-Your-Input Informational
// bucket. Mirrors agent/payload_v4.py::InformationalItem.
export interface InformationalItemView {
  id: string;
  text: string;
  narrative?: string | null;
  source_ref: string;
  source_author?: string | null;
  source_timestamp?: string | null;
  attribution?: string | null;
  // Origin context when relocated out of the tree by the guard — shows
  // the recipient where it surfaced. Null when synth emitted it straight
  // to the bucket.
  origin_thread_slug?: string | null;
  origin_thread_name?: string | null;
  origin_work_item_slug?: string | null;
  origin_work_item_name?: string | null;
  is_new?: boolean;
  // A³/A² (2026-06-03): set on entries in informational_acknowledged[].
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
}

export interface BriefingRecord {
  project_slug: string;
  run_id: string;
  week_start: string;
  week_end: string;
  stored_at: string;
  payload: BriefingPayload;
}

const SECTION_ORDER: Section[] = [
  "completed",
  "milestones",
  "in_progress",
  "decisions",
  "blockers",
  "action_items",
  "open_questions",
  "leadership_attention",
];

const SECTION_LABEL: Record<Section, string> = {
  completed: "Completed",
  milestones: "Milestones",
  in_progress: "In progress",
  decisions: "Decisions",
  blockers: "Blockers",
  action_items: "Action items",
  open_questions: "Open questions",
  leadership_attention: "Requires your attention",
};

export async function fetchBriefingIndex(): Promise<BriefingListItem[]> {
  const res = await getJson<{ briefings: BriefingListItem[] }>("/briefings");
  return res.briefings;
}

export async function fetchBriefing(runId: string): Promise<BriefingRecord> {
  return getJson<BriefingRecord>(`/briefings/${encodeURIComponent(runId)}`);
}

export interface SubmittedFeedbackEvent {
  event_id: string;
  run_id: string;
  project_slug: string;
  kind: string;
  target?: { type: string; id: string };
  payload: Record<string, unknown>;
  submitted_at: string;
  submitted_by: string;
  via: string;
  // B7: false once poll-portal has drained the event into corrections.yaml —
  // then it's no longer retractable (reverse with the inverse correction).
  // Absent on legacy responses → treated as retractable (Worker is the
  // authoritative gate and 409s an absorbed event).
  pending?: boolean;
}

export async function fetchMyFeedback(
  runId: string,
): Promise<SubmittedFeedbackEvent[]> {
  const res = await getJson<{ events: SubmittedFeedbackEvent[] }>(
    `/feedback/${encodeURIComponent(runId)}`,
  );
  return res.events;
}

// B7: retract a pending feedback event. Resolves on 200; throws ApiError
// (409 already-absorbed / 403 / 404) otherwise so the caller can message.
export async function retractFeedback(
  runId: string, eventId: string,
): Promise<void> {
  await deleteJson<{ retracted: string }>(
    `/feedback/${encodeURIComponent(runId)}/${encodeURIComponent(eventId)}`,
  );
}

export async function sendReadReceipt(runId: string): Promise<void> {
  // Best-effort. Failure is silent — receipts are heartbeats, not blocking.
  await postEmpty(`/receipt/${encodeURIComponent(runId)}`, {}).catch(
    () => undefined,
  );
}

// --- Rendering ---------------------------------------------------------------

export function formatStoredAt(iso: string): string {
  // Disambiguate same-week reruns in the index hover. Format as
  // "YYYY-MM-DD HH:MM" in the viewer's local zone — precise enough that
  // multiple reruns of the same week_end show distinct timestamps,
  // readable enough that operators can correlate with their own clock.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}


// Decision 12.11 (2026-05-28, client feedback #8): TOC anchor id format.
// Used by both the thread-header DOM id and the TOC link href.
export function categoryAnchorId(slug: string): string {
  return `category-${slug}`;
}

// #37: anchor for a phase band header, so renderTocV4 can jump to a band.
// Distinct namespace from `category-<slug>` (a band is not a Category).
export function phaseAnchorId(phase: PhaseValue): string {
  return `phase-${phase}`;
}

// Decision 12.15 (2026-05-28): Needs Your Input TOC anchor. Distinct
// from `category-<slug>` because the Needs Your Input section is a
// peer of the Category tree, not a Category itself.
export const NEEDS_INPUT_ANCHOR_ID = "needs-your-input";

// Decision 12.11 (2026-05-28, client feedback #8): table-of-contents
// listing every visible Category in the current briefing, rendered
// below the briefing list in the sidebar. Click → anchor-scroll to
// the Category header. The active Category is NOT highlighted (no
// scroll-spy in v1; can layer in later if recipients ask).
//
// Decision 12.15 (2026-05-28): Needs Your Input section is included
// when present, prepended above the Category list. Distinct anchor
// id (NEEDS_INPUT_ANCHOR_ID) so it doesn't collide with a Category
// slug that happens to be "needs-your-input".
export interface TocCategory {
  slug: string;
  display_number?: string | null;
  name: string;
  // #37: the Category's phase band, for grouping the TOC under phase headers.
  phase?: PhaseValue | null;
}

export function renderTocV4(
  el: HTMLElement,
  categories: TocCategory[],
  options: { hasNeedsInput?: boolean } = {},
): void {
  if (!categories.length && !options.hasNeedsInput) return;

  const heading = document.createElement("h2");
  heading.textContent = "Categories";
  el.appendChild(heading);

  const list = document.createElement("ul");
  list.className = "toc-list";

  // Per-entry anchor-scroll handler factored out so the Needs Your
  // Input row can share it.
  const buildRow = (
    href: string, anchorId: string, label: string, titleAttr: string,
  ): HTMLElement => {
    const li = document.createElement("li");
    const a = document.createElement("a");
    a.href = href;
    a.textContent = label;
    a.title = titleAttr;
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const target = document.getElementById(anchorId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        history.replaceState(null, "", href);
      }
    });
    li.appendChild(a);
    return li;
  };

  const buildCategoryRow = (c: TocCategory): HTMLElement => {
    const num = c.display_number ? `${c.display_number}. ` : "";
    return buildRow(
      `#${categoryAnchorId(c.slug)}`,
      categoryAnchorId(c.slug),
      `${num}${c.name}`,
      c.name,
    );
  };

  // #37: group the TOC under phase section headers when phasing is ACTIVE
  // (≥1 category in a non-general band). When everything is General
  // (unphased/legacy project) the TOC stays flat — byte-identical to the
  // pre-#37 list, no lone "General" header. Empty bands are omitted from the
  // TOC even under always-show: the TOC is a jump-list to content, not the
  // Brief structure itself. Within a band the numeric order matches the body.
  const byBand = new Map<PhaseValue, TocCategory[]>();
  for (const c of categories) {
    const band = phaseBand(c.phase);
    const arr = byBand.get(band);
    if (arr) arr.push(c);
    else byBand.set(band, [c]);
  }
  const phasingActive = PHASE_ORDER.some(
    (p) => p !== "general" && (byBand.get(p)?.length ?? 0) > 0,
  );

  if (phasingActive) {
    for (const phase of PHASE_ORDER) {
      const inBand = (byBand.get(phase) ?? []).slice().sort(
        (a, b) => compareDisplayNumber(a.display_number, b.display_number),
      );
      if (!inBand.length) continue;
      const sectionLi = document.createElement("li");
      sectionLi.className = "toc-phase-header";
      const sectionLink = document.createElement("a");
      sectionLink.href = `#${phaseAnchorId(phase)}`;
      sectionLink.textContent = PHASE_LABELS[phase];
      sectionLink.addEventListener("click", (e) => {
        e.preventDefault();
        const target = document.getElementById(phaseAnchorId(phase));
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
          history.replaceState(null, "", `#${phaseAnchorId(phase)}`);
        }
      });
      sectionLi.appendChild(sectionLink);
      list.appendChild(sectionLi);
      for (const c of inBand) list.appendChild(buildCategoryRow(c));
    }
  } else {
    for (const c of categories) list.appendChild(buildCategoryRow(c));
  }

  // Needs Your Input goes LAST in the TOC — mirrors the body order
  // where the section is at the bottom of the briefing. Operator
  // direction (2026-05-28 follow-up). Dashed divider ABOVE
  // separates it from the Category list.
  if (options.hasNeedsInput) {
    const li = buildRow(
      `#${NEEDS_INPUT_ANCHOR_ID}`,
      NEEDS_INPUT_ANCHOR_ID,
      "Needs Your Input",
      "Things the agent couldn't place or decide — you decide what each is",
    );
    li.classList.add("toc-needs-input");
    list.appendChild(li);
  }
  el.appendChild(list);
}

// Decision 12.10 (2026-05-28, client feedback #7): briefing list shows
// at most BRIEFING_LIST_PAGE_SIZE entries by default. If there are more,
// a "Show N older briefings ↓" button reveals the rest. The active
// briefing is ALWAYS visible — if it lives outside the first page, the
// expand state defaults to expanded so the recipient sees their
// current location without an extra click.
export const BRIEFING_LIST_PAGE_SIZE = 8;

export function renderBriefingIndex(
  el: HTMLElement,
  briefings: BriefingListItem[],
  onPick: (runId: string) => void,
  activeRunId?: string,
): void {
  el.innerHTML = "";
  const heading = document.createElement("h2");
  heading.textContent = "Briefings";
  el.appendChild(heading);

  if (briefings.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "No briefings yet.";
    el.appendChild(empty);
    return;
  }
  const list = document.createElement("ul");
  list.className = "briefing-list";

  const appendRow = (b: BriefingListItem): void => {
    const li = document.createElement("li");
    if (activeRunId === b.run_id) li.classList.add("active");
    const a = document.createElement("a");
    a.href = `${apiPathPrefix()}/runs/${encodeURIComponent(b.run_id)}`;
    a.textContent = `Week ending ${b.week_end}`;
    a.title = `${b.week_start} – ${b.week_end} · generated ${formatStoredAt(b.stored_at)}`;
    a.addEventListener("click", (e) => {
      e.preventDefault();
      onPick(b.run_id);
    });
    li.appendChild(a);
    list.appendChild(li);
  };

  const activeIndex = activeRunId
    ? briefings.findIndex((b) => b.run_id === activeRunId)
    : -1;
  const exceedsPage = briefings.length > BRIEFING_LIST_PAGE_SIZE;
  // Default expanded if the active briefing falls past the page cutoff.
  const startExpanded = exceedsPage && activeIndex >= BRIEFING_LIST_PAGE_SIZE;

  const initial = exceedsPage && !startExpanded
    ? briefings.slice(0, BRIEFING_LIST_PAGE_SIZE)
    : briefings;
  for (const b of initial) appendRow(b);
  el.appendChild(list);

  if (exceedsPage && !startExpanded) {
    const more = document.createElement("button");
    more.type = "button";
    more.className = "briefing-list-more";
    const remaining = briefings.length - BRIEFING_LIST_PAGE_SIZE;
    more.textContent = `Show ${remaining} older ↓`;
    more.setAttribute(
      "aria-label",
      `Show ${remaining} older briefings`,
    );
    more.addEventListener("click", () => {
      for (const b of briefings.slice(BRIEFING_LIST_PAGE_SIZE)) {
        appendRow(b);
      }
      more.remove();
    });
    el.appendChild(more);
  }
}

export function indexFeedbackByTarget(
  events: SubmittedFeedbackEvent[],
): Map<string, SubmittedFeedbackEvent[]> {
  const map = new Map<string, SubmittedFeedbackEvent[]>();
  for (const ev of events) {
    if (ev.kind === "read_receipt") continue;
    const id = ev.target?.id ?? "";
    if (!id) continue;
    const list = map.get(id) ?? [];
    list.push(ev);
    map.set(id, list);
  }
  return map;
}

export function indexCorrectionsByTarget(
  corrections: Correction[] | undefined,
): Map<string, Correction[]> {
  const map = new Map<string, Correction[]>();
  if (!corrections) return map;
  for (const c of corrections) {
    const id = c.target?.id;
    if (!id) continue;
    const list = map.get(id) ?? [];
    list.push(c);
    map.set(id, list);
  }
  return map;
}

// Slice 8 Item #3 — compactness toggle state. `recommended` defers to
// each work-item's synth-emitted `compact` flag; `compact` forces every
// work-item compact; `expanded` forces every work-item expanded.
// Persisted in localStorage so the operator's preference survives
// refreshes / navigation.
export type CompactnessMode = "recommended" | "compact" | "expanded";
const COMPACTNESS_KEY = "compactness-mode";
const COMPACTNESS_OVERRIDE_PREFIX = "compactness-override:";

export function readCompactnessMode(): CompactnessMode {
  try {
    const raw = localStorage.getItem(COMPACTNESS_KEY);
    if (raw === "compact" || raw === "expanded" || raw === "recommended") {
      return raw;
    }
  } catch {
    // localStorage unavailable (private browsing on some browsers).
  }
  return "recommended";
}

export function writeCompactnessMode(mode: CompactnessMode): void {
  try {
    localStorage.setItem(COMPACTNESS_KEY, mode);
  } catch {
    // ignore
  }
}

export function readCompactnessOverride(
  runId: string,
  targetId: string,
): "compact" | "expanded" | null {
  try {
    const raw = localStorage.getItem(
      `${COMPACTNESS_OVERRIDE_PREFIX}${runId}:${targetId}`,
    );
    if (raw === "compact" || raw === "expanded") return raw;
  } catch {
    // ignore
  }
  return null;
}

export function writeCompactnessOverride(
  runId: string,
  targetId: string,
  override: "compact" | "expanded" | null,
): void {
  try {
    const key = `${COMPACTNESS_OVERRIDE_PREFIX}${runId}:${targetId}`;
    if (override === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, override);
    }
  } catch {
    // ignore
  }
}

/** Compute the effective compactness for a work-item: per-item
 *  override wins; else global mode; else the synth's `compact` hint
 *  (under "recommended"). */
export function effectiveCompactness(
  globalMode: CompactnessMode,
  override: "compact" | "expanded" | null,
  synthHint: boolean | undefined,
): boolean {
  if (override) return override === "compact";
  if (globalMode === "compact") return true;
  if (globalMode === "expanded") return false;
  return synthHint === true;
}

// Slice 8 Item #5 — participant options for the responsible_party_assign
// form's <datalist>. Three sources, merged + deduped by name:
//   1. Briefing body: names appearing in any work_item's responsible_party,
//      involved, or category entries' owner/assignee/decided_by/raised_by.
//   2. payload.project.team_members: configured project roster from
//      config/projects/<slug>.yaml (injected by the agent harness).
//   3. payload.project.participants: observed-in-traffic roster from
//      state.db's project_participants table (injected by the harness).
// Email metadata: takes the first non-null email seen per name. Sources
// 2 + 3 carry emails directly; source 1 only carries email from
// responsible_party.email. The ParticipantOption type lives in forms.ts
// where it pairs with FormControlSpec.
export function collectParticipantOptions(
  payload: BriefingPayload,
): ParticipantOption[] {
  const byName = new Map<string, ParticipantOption>();
  const ingest = (name: string | null | undefined, email?: string | null) => {
    if (!name) return;
    const key = name.trim();
    if (!key) return;
    const existing = byName.get(key);
    if (existing) {
      if (!existing.email && email) existing.email = email;
      return;
    }
    byName.set(key, { name: key, email: email ?? null });
  };

  const visitWorkItem = (wi: WorkItemView): void => {
    if (wi.responsible_party?.name) {
      ingest(wi.responsible_party.name, wi.responsible_party.email);
    }
    for (const name of wi.involved ?? []) ingest(name);
    const sections: AnyItem[][] = [
      wi.completed ?? [], wi.milestones ?? [], wi.in_progress ?? [],
      wi.decisions ?? [], wi.blockers ?? [], wi.action_items ?? [],
      wi.open_questions ?? [], wi.leadership_attention ?? [],
    ];
    for (const sec of sections) {
      for (const item of sec) {
        ingest(item.owner);
        ingest(item.assignee);
        ingest(item.decided_by);
        ingest(item.raised_by);
      }
    }
  };

  for (const thread of payload.threads ?? []) {
    for (const wi of thread.work_items ?? []) visitWorkItem(wi);
  }
  for (const wi of payload.unthreaded?.work_items ?? []) visitWorkItem(wi);

  // Roster seeds from the agent harness. team_members is the
  // operator-curated project YAML roster; participants is the
  // observed-in-traffic registry. Both feed the same dropdown so the
  // operator can pick a person even when they haven't appeared in
  // *this* briefing yet.
  for (const tm of payload.project.team_members ?? []) {
    ingest(tm.name, tm.email);
  }
  for (const p of payload.project.participants ?? []) {
    ingest(p.name, p.email);
  }

  return Array.from(byName.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

/** Numeric-aware compare of display numbers ("2.9" < "2.10" < "10"); null/
 * absent sort last. Orders the Category + Item pickers by their number
 * rather than alphabetically (#63 follow-up, 2026-06-09). */
/** #66 D1-D3 (2026-07-23): resolve a merge_candidate's counterpart(s) to
 * mergeable OPEN Items rendered this week, for the ✎ menu's one-click merge
 * banner. Pure — unit-tested directly.
 *
 * Structured path (`target_locators`, blobs from the first post-promote cron
 * on): an Action target's mergeable Item is its PARENT (`loc.item`); an Item
 * target is itself. A locator whose item slug isn't in `itemOptions` is
 * DROPPED — the consult stamps before demote/informational-relocate, so a
 * target can leave the rendered tree (correctness requirement, not polish).
 *
 * Labels-only degrade (every blob until that cron): one-click only on an
 * EXACT, UNIQUE, untruncated name match — a "…"-truncated label or an
 * ambiguous name never resolves (accept must not guess; the inline note +
 * menu nudge remain). Self-targets are always excluded. */
export function resolveMergeCandidateTargets(
  mc: MergeCandidateView | null | undefined,
  selfSlug: string,
  itemOptions: ItemOption[],
): Array<{ slug: string; label: string }> {
  if (!mc) return [];
  const out: Array<{ slug: string; label: string }> = [];
  const seen = new Set<string>();
  const push = (opt: ItemOption) => {
    if (opt.slug === selfSlug || seen.has(opt.slug)) return;
    seen.add(opt.slug);
    out.push({
      slug: opt.slug,
      label: opt.display_number ? `${opt.display_number} ${opt.name}` : opt.name,
    });
  };
  const locators = mc.target_locators ?? [];
  if (locators.length > 0) {
    for (const loc of locators) {
      if (!loc?.item) continue;
      const opt = itemOptions.find((o) => o.slug === loc.item);
      if (opt) push(opt);
    }
    return out;
  }
  for (const label of mc.targets ?? []) {
    if (!label || label.endsWith("…")) continue;
    const norm = label.trim().toLowerCase();
    const matches = itemOptions.filter((o) => o.name.trim().toLowerCase() === norm);
    const only = matches[0];
    if (matches.length === 1 && only) push(only);
  }
  return out;
}

/** #66 D1-D3: localStorage dismiss key for an Item's merge-proposal banner +
 * inline notes. Matter-only proposals keep the bare target id (back-compat —
 * Ray's existing dismissals stay honored); an overlap-bearing proposal gets a
 * distinct `:overlap` suffix so a previously dismissed matter hint does NOT
 * silently hide a NEW cross-source overlap signal (it resurfaces once). */
export function mergeBannerDismissKey(
  targetId: string,
  hasOverlap: boolean,
): string {
  return hasOverlap ? `${targetId}:overlap` : targetId;
}

export function compareDisplayNumber(
  a?: string | null, b?: string | null,
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const pa = a.split(".");
  const pb = b.split(".");
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = Number(pa[i] ?? 0) || 0;
    const y = Number(pb[i] ?? 0) || 0;
    if (x !== y) return x - y;
  }
  return 0;
}

// Build a {slug, name, display_number} list of Categories on this briefing
// for the move/merge target pickers (thread_merge, item_move_to_category,
// place_*). Only CONFIRMED, OPEN Categories are targets:
//  - closed/retired are skipped (you can't move/merge into a retired one);
//  - candidate_threads (inferred "Suggested Categories") are EXCLUDED — a
//    proposal isn't a real Category and must not be a move/merge target
//    until it's promoted (client feedback 2026-06-09; this reverses the
//    earlier contract-drift batch-2 inclusion). Promotion happens via the
//    candidate's own Needs-Your-Input "place as Category" affordance.
// Ordered by Category number (#63 follow-up), name as the tiebreaker.
export function collectThreadOptions(
  payload: BriefingPayload,
): ThreadOptionView[] {
  const out: ThreadOptionView[] = [];
  const seen = new Set<string>();
  const push = (
    slug: string | undefined, name: string | undefined,
    display_number?: string | null,
    subcategories?: { slug: string; name: string }[],
  ) => {
    if (!slug || !name) return;
    if (seen.has(slug)) return;
    seen.add(slug);
    out.push({ slug, name, display_number: display_number ?? null, subcategories });
  };
  for (const t of payload.threads ?? []) {
    if (t.status === "closed" || t.status === "retired") continue;
    // #75: carry the Category's OPEN sub-categories for the deep-move picker.
    const openSubcats = (t.subcategories ?? [])
      .filter((s) => s.status !== "closed")
      .map((s) => ({ slug: s.slug, name: s.name }));
    push(t.slug, t.name, t.display_number, openSubcats);
  }
  return out.sort((a, b) =>
    compareDisplayNumber(a.display_number, b.display_number)
    || a.name.localeCompare(b.name));
}

// Local re-export of the forms.ts ThreadOption shape; defined here so
// briefings.ts doesn't take a runtime dep on forms.ts (the test suite
// imports collectThreadOptions standalone).
export interface ThreadOptionView {
  slug: string;
  name: string;
  display_number?: string | null;
  // #75: this Category's OPEN sub-categories, so the move/place picker can
  // offer "Category › Sub-category" destinations and file in one step.
  subcategories?: { slug: string; name: string }[];
}

export function renderBriefingViewer(
  el: HTMLElement,
  record: BriefingRecord,
  feedbackByTarget?: Map<string, SubmittedFeedbackEvent[]>,
): void {
  el.innerHTML = "";
  const fb = feedbackByTarget ?? new Map<string, SubmittedFeedbackEvent[]>();
  // Corrections raised against THIS run_id (delivered via the agent's
  // briefing envelope at agent/portal.py::_corrections_for_run). Indexed
  // by target.id so each row can decorate itself; corrections without a
  // target.id ride along but aren't rendered here.
  const corr = indexCorrectionsByTarget(record.payload.corrections);

  // Slice 7 L3 feedback (2026-05-10): the download action moved from
  // a prominent text link here to a compact icon in the shell header
  // next to "Week ending {date}". `buildDownloadIcon` in app.ts owns
  // that render; the viewer leaves docx_b64 to the icon to surface.

  // #52b recoverability: render the Executive summary section whenever there IS a
  // summary OR the briefing is v4 (so the "Edit summary" pencil is always present
  // on v4). Without the v4 clause, clearing the summary made the section falsy →
  // the pencil vanished → no SPA path to re-add (recovery needed operator CLI).
  // v3 (immutable historical) keeps the prior behaviour: render only if present.
  const isV4Summary = record.payload.payload_schema_version === 4;
  const summaryText = record.payload.executive_summary;
  if (summaryText || isV4Summary) {
    const summary = document.createElement("section");
    summary.className = "summary";
    const h = document.createElement("h2");
    h.textContent = "Executive summary";
    // R2-3 fast-follow: a recipient can edit the executive summary directly (v4
    // only — v3 are immutable historical snapshots). The summary is a project-
    // level singleton, so the control targets `project:<slug>` (like the Add
    // Category pencil) and carries a single `set_executive_summary` kind,
    // prefilled with the current text (blank when empty → an "add" form). The
    // edit is reasserted onto the node every run (overrides the synth's
    // regenerated §14 summary). The title + pencil share a grid header row
    // (.summary-header), mirroring .thread-header — the `.controls` needs a
    // positioned ancestor or its popover escapes to the viewport corner (the L3
    // round-3 bug documented in styles.css).
    if (isV4Summary) {
      const header = document.createElement("div");
      header.className = "summary-header";
      header.appendChild(h);
      const summaryTargetId = `project:${record.project_slug}`;
      const editControls = renderFormControls({
        runId: record.run_id,
        targetType: "project",
        targetId: summaryTargetId,
        kinds: ["set_executive_summary"],
        // Filter to THIS control's kind: the Add-Category pencil shares the same
        // `project:<slug>` targetId, so an unfiltered list would cross-show the
        // other control's chips/badge.
        submitted: (fb.get(summaryTargetId) ?? []).filter(
          (e) => e.kind === "set_executive_summary",
        ),
        current: { summary: summaryText },
      });
      editControls.classList.add("v4-edit-summary-control");
      header.appendChild(editControls);
      summary.appendChild(header);
    } else {
      summary.appendChild(h);
    }
    const p = document.createElement("p");
    if (summaryText) {
      p.textContent = summaryText;
    } else {
      // Empty (cleared, or none produced) — a muted placeholder; the pencil above
      // lets the recipient add one.
      p.textContent = "No executive summary this period.";
      p.className = "muted";
    }
    summary.appendChild(p);
    el.appendChild(summary);
  }

  const isV2 = record.payload.payload_schema_version === 2;
  const isV3 = record.payload.payload_schema_version === 3;
  const isV4 = record.payload.payload_schema_version === 4;

  // v4 takes a different shape from v2/v3 (Category → Item → Action
  // with explicit display numbers, priority enum, Open Questions
  // sub-section, Unfiled top-level). Dispatch through the parallel
  // renderer and return early; v4 doesn't reuse v3's thread/unthreaded/
  // candidate_threads helpers.
  if (isV4) {
    // Reuse the v3 collectors for participants + threads — they walk
    // the payload's people / thread fields and the SPA's responsible_
    // party_assign / thread_merge / move-to-category forms need the
    // same datalists.
    const v4Participants = collectParticipantOptions(record.payload);
    const v4Threads = collectThreadOptions(record.payload);
    renderBriefingV4(el, record, fb, corr, v4Participants, v4Threads);
    return;
  }

  // Participant options for responsible_party_assign — built once
  // per render from the payload's people fields. Passed to each v3
  // renderer below so the popover's form can populate a datalist.
  const participantOptions = isV3 ? collectParticipantOptions(record.payload) : [];

  // Thread options for thread_merge (thread-header) and thread_retag
  // (per-item-row). Same build-once-per-render shape as
  // participantOptions. V1/V2 paths fall back to free-text input plus
  // the slugify safety net in forms.ts::reshapePayloadForKind.
  const threadOptions = isV3 ? collectThreadOptions(record.payload) : [];

  // Re-render closure used by every interactive control that mutates
  // a localStorage flag (global compactness toggle, per-work-item
  // override). Invoking it clears the viewer and re-runs this
  // function against the same record.
  const rerender = (): void => {
    renderBriefingViewer(el, record, fb);
  };

  // Slice 8 Item #3 — v3 viewer mounts a global compactness toggle.
  // Clicking re-renders the viewer in place using the new mode.
  if (isV3) {
    el.appendChild(renderCompactnessToggle(rerender));
  }

  for (const thread of record.payload.threads ?? []) {
    if (isV3) {
      el.appendChild(renderThreadV3(record.run_id, thread, fb, corr, participantOptions, threadOptions, rerender));
    } else if (isV2) {
      el.appendChild(renderThreadV2(record.run_id, thread, fb, corr));
    } else {
      el.appendChild(renderThread(record.run_id, thread, fb, corr));
    }
  }

  if (record.payload.unthreaded) {
    if (isV3) {
      el.appendChild(renderUnthreadedV3(record.run_id, record.payload.unthreaded, fb, corr, participantOptions, threadOptions, rerender));
    } else if (isV2) {
      el.appendChild(renderUnthreadedV2(record.run_id, record.payload.unthreaded, fb, corr));
    } else {
      el.appendChild(renderUnthreaded(record.run_id, record.payload.unthreaded, fb, corr));
    }
  } else if (
    !(record.payload.threads ?? []).length &&
    isFlatLegacyPayload(record.payload)
  ) {
    // Pre-threads-refactor briefings (synthesizer output before
    // `feat(threads): organize briefings by sub-project thread`) have
    // section keys at the top level of payload — no `threads`, no
    // `unthreaded`. The SPA otherwise renders nothing for them. Route
    // the flat payload through renderUnthreaded with an "All items"
    // heading (vs the threads-schema "Cross-cutting" label) so the
    // section accurately describes its contents — there's no thread
    // breakdown to be cross-cutting against.
    el.appendChild(
      renderUnthreaded(
        record.run_id,
        record.payload as Partial<Record<Section, AnyItem[]>>,
        fb, corr,
        "All items",
      ),
    );
  }

  // Slice 8 Item #1 Layer B — candidate threads bucket. v3 renders
  // them between threads and needs_review with thread_promote /
  // thread_reject / comment controls; older payloads currently render
  // nothing (the markdown template handles them separately).
  if (isV3 && record.payload.candidate_threads?.length) {
    el.appendChild(
      renderCandidateThreadsV3(record.run_id, record.payload.candidate_threads, fb, corr),
    );
  }

  if (record.payload.needs_review?.length) {
    el.appendChild(renderNeedsReview(record.run_id, record.payload.needs_review, fb, corr));
  }
}

/** Detect briefings synthesized before the threads refactor. They have
 * `action_items` / `blockers` / etc. at the top level instead of inside
 * a `threads` or `unthreaded` block. Exported for unit test. */
export function isFlatLegacyPayload(p: BriefingRecord["payload"]): boolean {
  const flatKeys: Section[] = [
    "action_items", "blockers", "completed", "decisions",
    "in_progress", "leadership_attention", "milestones", "open_questions",
  ];
  const bag = p as unknown as Record<string, unknown>;
  return flatKeys.some((k) => Array.isArray(bag[k]));
}

function renderThread(
  runId: string,
  thread: ThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread";

  const header = document.createElement("div");
  header.className = "thread-header";
  const h2 = document.createElement("h2");
  h2.textContent = thread.name;
  // Thread-level corrections (rename / merge / retag / approve raised
  // via the form path with target.id = "thread:<slug>") get a badge
  // alongside the title.
  const threadTargetId = threadKey(thread.slug);
  const threadBadge = renderCorrectedBadge(corr.get(threadTargetId));
  if (threadBadge) h2.appendChild(threadBadge);
  header.appendChild(h2);
  if (thread.one_liner) {
    const p = document.createElement("p");
    p.className = "muted";
    p.textContent = thread.one_liner;
    header.appendChild(p);
  }
  // Thread-level controls: rename, merge, retag, approve.
  const threadControls = renderFormControls({
    runId,
    targetType: "thread",
    targetId: threadTargetId,
    kinds: [
      "thread_approve",
      "thread_rename",
      "thread_merge",
      "comment",
    ],
    submitted: fb.get(threadTargetId) ?? [],
  });
  header.appendChild(threadControls);
  section.appendChild(header);

  for (const sec of SECTION_ORDER) {
    const items = thread[sec];
    if (!items?.length) continue;
    section.appendChild(
      renderSection(runId, items, sec, (i) =>
        threadItemKey(thread.slug, sec, i),
        fb, corr,
      ),
    );
  }
  return section;
}

function renderUnthreaded(
  runId: string,
  unthreaded: Partial<Record<Section, AnyItem[]>>,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  heading: string = "Cross-cutting",
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread unthreaded";
  const h2 = document.createElement("h2");
  h2.textContent = heading;
  section.appendChild(h2);

  let any = false;
  for (const sec of SECTION_ORDER) {
    const items = unthreaded[sec];
    if (!items?.length) continue;
    any = true;
    section.appendChild(
      renderSection(runId, items, sec, (i) => unthreadedItemKey(sec, i), fb, corr),
    );
  }
  if (!any) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "Nothing here this week.";
    section.appendChild(empty);
  }
  return section;
}

// --- Slice 7 v2 render path -------------------------------------------------

function renderThreadV2(
  runId: string,
  thread: ThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread thread-v2";

  const header = document.createElement("div");
  header.className = "thread-header";
  const h2 = document.createElement("h2");
  h2.textContent = thread.name;
  const threadTargetId = threadKey(thread.slug);
  const threadBadge = renderCorrectedBadge(corr.get(threadTargetId));
  if (threadBadge) h2.appendChild(threadBadge);
  header.appendChild(h2);
  if (thread.one_liner) {
    const p = document.createElement("p");
    p.className = "muted";
    p.textContent = thread.one_liner;
    header.appendChild(p);
  }
  // Thread-level controls. The "Close thread" action is gated by the
  // open work-item count — the SPA shows a notice inside the popover
  // (instead of polluting the thread header with always-on text) and
  // the Worker rejects an unsafe POST as defense in depth.
  const openCount = (thread.work_items ?? []).filter(
    (w) => (w.status ?? "open") === "open",
  ).length;
  const threadKinds: string[] = [
    "thread_approve", "thread_rename", "thread_merge", "comment",
  ];
  if (thread.status === "closed") {
    threadKinds.push("thread_reopen");
  } else {
    threadKinds.push("thread_close");
  }
  const threadControls = renderFormControls({
    runId,
    targetType: "thread",
    targetId: threadTargetId,
    kinds: threadKinds,
    submitted: fb.get(threadTargetId) ?? [],
    notice: openCount > 0 && thread.status !== "closed"
      ? `Close ${openCount} open work item${openCount === 1 ? "" : "s"} before closing this thread.`
      : undefined,
  });
  header.appendChild(threadControls);
  section.appendChild(header);

  for (const wi of thread.work_items ?? []) {
    section.appendChild(renderWorkItem(runId, thread.slug, wi, fb, corr));
  }

  if ((thread.archived_work_items ?? []).length > 0) {
    section.appendChild(renderArchivedWorkItems(thread));
  }

  return section;
}

function renderWorkItem(
  runId: string,
  threadSlug: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"}`;

  const header = document.createElement("div");
  header.className = "work-item-header";

  const h3 = document.createElement("h3");
  h3.textContent = wi.name;
  const targetId = workItemKey(threadSlug, wi.slug);
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) h3.appendChild(badge);
  header.appendChild(h3);

  if (wi.summary) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    header.appendChild(p);
  }
  if (wi.involved?.length) {
    const meta = document.createElement("p");
    meta.className = "muted";
    meta.textContent = `Involved: ${wi.involved.join(", ")}`;
    header.appendChild(meta);
  }
  if (wi.key_dates?.length) {
    const meta = document.createElement("p");
    meta.className = "muted";
    meta.textContent = `Key dates: ${wi.key_dates.join(" · ")}`;
    header.appendChild(meta);
  }

  const wiKinds: string[] = ["comment", "flag_next_week"];
  if (wi.status === "closed") {
    wiKinds.unshift("work_item_reopen");
  } else {
    wiKinds.unshift("work_item_close");
  }
  header.appendChild(renderFormControls({
    runId,
    targetType: "work_item",
    targetId,
    kinds: wiKinds,
    submitted: fb.get(targetId) ?? [],
  }));
  wrapper.appendChild(header);

  for (const sec of SECTION_ORDER) {
    const items = wi[sec];
    if (!items?.length) continue;
    wrapper.appendChild(
      renderSection(runId, items, sec, (i) =>
        workItemSectionItemKey(threadSlug, wi.slug, sec, i),
        fb, corr,
      ),
    );
  }
  return wrapper;
}

function renderArchivedWorkItems(thread: ThreadView): HTMLElement {
  const details = document.createElement("details");
  details.className = "archived-work-items";
  const summary = document.createElement("summary");
  const count = (thread.archived_work_items ?? []).length;
  summary.textContent = `Archived work items (${count})`;
  details.appendChild(summary);
  const ul = document.createElement("ul");
  for (const ar of thread.archived_work_items ?? []) {
    const li = document.createElement("li");
    const name = document.createElement("strong");
    name.textContent = ar.name;
    li.appendChild(name);
    const meta = document.createElement("span");
    meta.className = "muted";
    const bits: string[] = [];
    if (ar.closed_at) bits.push(`closed ${ar.closed_at}`);
    if (ar.closed_reason) bits.push(ar.closed_reason);
    if (bits.length) meta.textContent = " — " + bits.join(": ");
    li.appendChild(meta);
    ul.appendChild(li);
  }
  details.appendChild(ul);
  return details;
}

function renderUnthreadedV2(
  runId: string,
  unthreaded: NonNullable<BriefingPayload["unthreaded"]>,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread unthreaded thread-v2";
  const h2 = document.createElement("h2");
  h2.textContent = "Cross-cutting";
  section.appendChild(h2);

  const workItems = unthreaded.work_items ?? [];
  for (const wi of workItems) {
    section.appendChild(renderUnthreadedWorkItem(runId, wi, fb, corr));
  }
  if (!workItems.length) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "Nothing here this week.";
    section.appendChild(empty);
  }
  return section;
}

function renderUnthreadedWorkItem(
  runId: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"}`;
  const h3 = document.createElement("h3");
  h3.textContent = wi.name;
  const targetId = unthreadedWorkItemKey(wi.slug);
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) h3.appendChild(badge);
  wrapper.appendChild(h3);

  if (wi.summary) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    wrapper.appendChild(p);
  }

  for (const sec of SECTION_ORDER) {
    const items = wi[sec];
    if (!items?.length) continue;
    wrapper.appendChild(
      renderSection(runId, items, sec, (i) =>
        unthreadedWorkItemSectionItemKey(wi.slug, sec, i),
        fb, corr,
      ),
    );
  }
  return wrapper;
}

// --- Slice 8 v3 render path -------------------------------------------------

function renderCompactnessToggle(onChange: () => void): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "compactness-toggle";
  const label = document.createElement("span");
  label.textContent = "View:";
  wrapper.appendChild(label);
  const current = readCompactnessMode();
  for (const mode of ["recommended", "compact", "expanded"] as CompactnessMode[]) {
    const btn = document.createElement("button");
    btn.type = "button";
    // Display label "Auto" for the synth-driven per-item mode; the
    // internal key stays "recommended" so localStorage values written
    // by older builds keep working (read tolerates either).
    btn.textContent =
      mode === "recommended" ? "Auto" :
      mode === "compact" ? "Compact" : "Expanded";
    if (mode === current) btn.classList.add("active");
    btn.addEventListener("click", () => {
      writeCompactnessMode(mode);
      onChange();
    });
    wrapper.appendChild(btn);
  }
  return wrapper;
}

// Inline SVG icons for the per-work-item compactness toggle. Sized
// 14×14 to match the .btn-edit pencil; same stroke weight + corner
// rounding.
const CHEVRON_DOWN_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polyline points="6 9 12 15 18 9"/>' +
  "</svg>";
const CHEVRON_UP_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polyline points="6 15 12 9 18 15"/>' +
  "</svg>";
// Counter-clockwise rotation arrow (Lucide "rotate-ccw" style).
// Universal "restore default" affordance — tooltip says "Restore
// Auto" to tie back to the global "Auto" mode label.
const RESET_ARROW_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polyline points="1 4 1 10 7 10"/>' +
  '<path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>' +
  "</svg>";

/** Per-work-item compactness override toggle. Three states:
 *    • no override + currently compact   → chevron-down (action: expand)
 *    • no override + currently expanded  → chevron-up (action: compact)
 *    • override exists (either kind)     → reset-arrow (action: restore "Auto")
 *  Tooltip names the action. Lives in `.work-item-controls-row`
 *  alongside the pencil; pencil stays at the far right because it's
 *  the last flex child. */
function renderWorkItemCompactnessToggle(
  runId: string,
  targetId: string,
  override: "compact" | "expanded" | null,
  isCompact: boolean,
  rerender: () => void,
): HTMLElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn-edit work-item-toggle-btn";

  let svg: string;
  let title: string;
  let onClick: () => void;
  if (override !== null) {
    svg = RESET_ARROW_SVG;
    title = "Restore Auto";
    onClick = () => {
      writeCompactnessOverride(runId, targetId, null);
      rerender();
    };
  } else if (isCompact) {
    svg = CHEVRON_DOWN_SVG;
    title = "Expand this work item";
    onClick = () => {
      writeCompactnessOverride(runId, targetId, "expanded");
      rerender();
    };
  } else {
    svg = CHEVRON_UP_SVG;
    title = "Compact this work item";
    onClick = () => {
      writeCompactnessOverride(runId, targetId, "compact");
      rerender();
    };
  }

  btn.title = title;
  btn.setAttribute("aria-label", title);
  btn.innerHTML = svg;
  btn.addEventListener("click", onClick);
  return btn;
}

function renderThreadV3(
  runId: string,
  thread: ThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  rerender: () => void,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread thread-v3";

  const header = document.createElement("div");
  header.className = "thread-header";
  const h2 = document.createElement("h2");
  h2.textContent = thread.name;
  const threadTargetId = threadKey(thread.slug);
  const threadBadge = renderCorrectedBadge(corr.get(threadTargetId));
  if (threadBadge) h2.appendChild(threadBadge);
  header.appendChild(h2);
  if (thread.one_liner) {
    const p = document.createElement("p");
    p.className = "muted";
    p.textContent = thread.one_liner;
    header.appendChild(p);
  }
  // v3 thread controls: thread_confirm replaces thread_approve;
  // thread_reject is new. Close/reopen logic mirrors v2.
  const openCount = (thread.work_items ?? []).filter(
    (w) => (w.status ?? "open") === "open",
  ).length;
  const threadKinds: string[] = [
    "thread_confirm", "thread_reject", "thread_rename",
    "thread_merge", "comment",
  ];
  if (thread.status === "closed") {
    threadKinds.push("thread_reopen");
  } else {
    threadKinds.push("thread_close");
  }
  const threadControls = renderFormControls({
    runId,
    targetType: "thread",
    targetId: threadTargetId,
    kinds: threadKinds,
    submitted: fb.get(threadTargetId) ?? [],
    threadOptions,
    notice: openCount > 0 && thread.status !== "closed"
      ? `Close ${openCount} open work item${openCount === 1 ? "" : "s"} before closing this thread.`
      : undefined,
  });
  header.appendChild(threadControls);
  section.appendChild(header);

  const globalMode = readCompactnessMode();
  for (const wi of thread.work_items ?? []) {
    section.appendChild(renderWorkItemV3(runId, thread.slug, wi, fb, corr, globalMode, participantOptions, threadOptions, rerender));
  }

  if ((thread.archived_work_items ?? []).length > 0) {
    section.appendChild(renderArchivedWorkItems(thread));
  }

  return section;
}

function renderWorkItemV3(
  runId: string,
  threadSlug: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  globalMode: CompactnessMode,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  rerender: () => void,
): HTMLElement {
  const targetId = workItemKey(threadSlug, wi.slug);
  const override = readCompactnessOverride(runId, targetId);
  const isCompact = effectiveCompactness(globalMode, override, wi.compact);

  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"} ${isCompact ? "compact" : "expanded"}`;

  const header = document.createElement("div");
  header.className = "work-item-header";

  const h3 = document.createElement("h3");
  h3.textContent = wi.name;
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) h3.appendChild(badge);
  header.appendChild(h3);

  if (wi.summary) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    header.appendChild(p);
  }

  // Slice 8 Item #5 — Responsible Party line. v3 drops the prior
  // Involved/Key dates lines per client direction; payload still
  // carries `involved` and `key_dates` for historical re-renders
  // but v3 doesn't render them. Responsible Party is the only
  // people-line in v3.
  if (wi.responsible_party) {
    header.appendChild(renderResponsibleParty(wi.responsible_party));
  }

  // Right-aligned control cluster: per-work-item compactness toggle
  // followed by the pencil popover. Wrapped in a flex row pinned to
  // grid col 2 / row 1 via the CSS rule on .work-item-controls-row
  // so the pencil stays at the far right and the toggle sits to its
  // left.
  const wiKinds: string[] = ["responsible_party_assign", "comment", "flag_next_week"];
  if (wi.status === "closed") {
    wiKinds.unshift("work_item_reopen");
  } else {
    wiKinds.unshift("work_item_close");
  }
  const controlsRow = document.createElement("div");
  controlsRow.className = "work-item-controls-row";
  controlsRow.appendChild(
    renderWorkItemCompactnessToggle(runId, targetId, override, isCompact, rerender),
  );
  controlsRow.appendChild(renderFormControls({
    runId,
    targetType: "work_item",
    targetId,
    kinds: wiKinds,
    submitted: fb.get(targetId) ?? [],
    participantOptions,
  }));
  header.appendChild(controlsRow);
  wrapper.appendChild(header);

  // Compact-mode pending list. Renders when (a) effectively compact
  // AND (b) the synth populated `pending`. Empty pending in compact
  // mode just suppresses the list — the always-full surfaces below
  // still render if they have content.
  //
  // BACKLOG #29: a "Pending" header sits above the list so the reader
  // sees the section label, matching the visual treatment of every
  // other category h3.
  if (isCompact && wi.pending?.length) {
    const heading = document.createElement("h3");
    heading.className = "work-item-pending-heading";
    heading.textContent = "Pending";
    wrapper.appendChild(heading);
    const ul = document.createElement("ul");
    ul.className = "work-item-pending";
    for (const text of wi.pending) {
      const li = document.createElement("li");
      li.textContent = text;
      ul.appendChild(li);
    }
    wrapper.appendChild(ul);
  }

  for (const sec of SECTION_ORDER) {
    const items = wi[sec];
    if (!items?.length) continue;
    // BACKLOG #29: in compact view, action_items is always rendered
    // (was: only when at least one item was impact-bearing-non-stale)
    // so the reader sees every action item, including routine ones,
    // as a title-only list. Description suppression for action_items
    // in compact mode is handled inside renderSectionV3.
    const isAlwaysFull = sec === "leadership_attention" ||
      sec === "action_items";
    wrapper.appendChild(
      renderSectionV3(runId, items, sec, (i) =>
        workItemSectionItemKey(threadSlug, wi.slug, sec, i),
        fb, corr,
        isCompact,
        isAlwaysFull,
        threadOptions,
      ),
    );
  }

  // BACKLOG #28: closed AIs per work item. Always rendered (regardless
  // of compactness) because the serial trail is the discussability
  // signal — a reader scanning the WI card needs to see what came off.
  if (wi.archived_action_items?.length) {
    wrapper.appendChild(renderArchivedActionItems(wi.archived_action_items));
  }

  return wrapper;
}

function renderArchivedActionItems(
  items: ArchivedActionItemView[],
): HTMLElement {
  // Collapsed by default (mirrors the item-level "Archived work items"
  // <details>) so recently-closed Actions stay available on click without
  // dominating the briefing after a big close-out — e.g. the 6/1 cleanup put
  // 64 closed Actions in the 2-week window (2026-06-07 density fix).
  const section = document.createElement("details");
  section.className = "section section-archived-action-items";
  const summary = document.createElement("summary");
  summary.textContent = `Closed action items (${items.length})`;
  section.appendChild(summary);
  const list = document.createElement("ul");
  for (const ar of items) {
    const li = document.createElement("li");
    li.className = "row";
    const text = document.createElement("div");
    text.className = "row-text";
    const handle = document.createElement("span");
    handle.className = "ai-handle";
    handle.textContent = `#${String(ar.serial).padStart(3, "0")}`;
    text.appendChild(handle);
    text.appendChild(document.createTextNode(ar.text));
    li.appendChild(text);
    if (ar.closed_at || ar.closed_reason) {
      const meta = document.createElement("p");
      meta.className = "muted";
      const parts: string[] = [];
      if (ar.closed_at) parts.push(`closed ${ar.closed_at}`);
      if (ar.closed_reason) parts.push(ar.closed_reason);
      meta.textContent = parts.join(": ");
      li.appendChild(meta);
    }
    list.appendChild(li);
  }
  section.appendChild(list);
  return section;
}

/** Section renderer for v3. When compact AND not always-full, the
 *  whole section is suppressed via CSS (.work-item.compact > .section
 *  rule). Items that are impact-bearing non-stale action_items get
 *  the [Risk] badge prepended. */
function renderSectionV3(
  runId: string,
  items: AnyItem[],
  section: Section,
  buildId: (index: number) => string,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  isCompact: boolean,
  isAlwaysFull: boolean,
  threadOptions: ThreadOptionView[],
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = `section section-${section}${isAlwaysFull ? " always-full" : ""}`;
  const h3 = document.createElement("h3");
  h3.textContent = SECTION_LABEL[section];
  wrapper.appendChild(h3);

  const list = document.createElement("ul");
  // BACKLOG #29: in compact view, action_items renders every item
  // title-only — descriptions are suppressed below. Routine items
  // no longer fold into `pending`; the Action Items section is the
  // single place a reader scans for them. Impact-bearing items still
  // get the [Risk] badge.
  const compactActionList = isCompact && section === "action_items";
  items.forEach((item, idx) => {
    const li = document.createElement("li");
    li.className = "row";
    const targetId = buildId(idx);
    li.dataset.targetId = targetId;

    const text = document.createElement("div");
    text.className = "row-text";
    // BACKLOG #28: per-Work-Item serial handle (e.g. #007). Present
    // only on action_items where the pipeline upsert assigned a serial.
    // Bare #NNN inside the WI card; parent composition is the reader's
    // responsibility for cross-references (BACKLOG decision 2026-05-19).
    if (section === "action_items" && typeof item.serial === "number") {
      const aiHandle = document.createElement("span");
      aiHandle.className = "ai-handle";
      aiHandle.textContent = `#${String(item.serial).padStart(3, "0")}`;
      text.appendChild(aiHandle);
    }
    if (section === "action_items" && isImpactBearingNonStale(item)) {
      const riskBadge = document.createElement("span");
      riskBadge.className = "risk-badge";
      riskBadge.textContent = "Risk";
      text.appendChild(riskBadge);
    }
    text.appendChild(document.createTextNode(describeItem(item)));
    const badge = renderCorrectedBadge(corr.get(targetId));
    if (badge) text.appendChild(badge);
    li.appendChild(text);

    // Description rules:
    // - open_questions: never rendered (Slice 8 Item #7, template
    //   matches).
    // - action_items in compact view: suppressed per BACKLOG #29
    //   so the section reads as a scannable title-only list.
    // - everything else: render when present.
    if (
      item.description
      && section !== "open_questions"
      && !compactActionList
    ) {
      const desc = document.createElement("p");
      desc.className = "row-description";
      desc.textContent = item.description;
      li.appendChild(desc);
    }

    // Meta-line rules (assignee · due · owner · etc.):
    // - action_items in compact view: suppressed per BACKLOG #29 —
    //   "title only" means no meta strip either.
    // - everything else: render when describeMeta() returns content.
    const meta = compactActionList ? null : describeMeta(item);
    if (meta) {
      const m = document.createElement("p");
      m.className = "muted";
      m.textContent = meta;
      li.appendChild(m);
    }

    li.appendChild(
      renderFormControls({
        runId,
        targetType: "item",
        targetId,
        kinds: kindsForSection(section),
        submitted: fb.get(targetId) ?? [],
        threadOptions,
      }),
    );

    list.appendChild(li);
  });
  wrapper.appendChild(list);
  return wrapper;
}

function isImpactBearingNonStale(item: AnyItem): boolean {
  return !!(item.impact && item.impact.length > 0) && item.stale !== true;
}

function renderResponsibleParty(rp: ResponsiblePartyView): HTMLElement {
  const p = document.createElement("p");
  p.className = "work-item-responsible";
  const label = document.createElement("span");
  label.className = "muted";
  label.textContent = "Responsible:";
  p.appendChild(label);
  const name = document.createElement("span");
  name.className = "work-item-responsible-name";
  name.textContent = rp.name;
  p.appendChild(name);
  if (rp.source === "default_pm") {
    const tag = document.createElement("span");
    tag.className = "work-item-responsible-source";
    tag.textContent = "(PM default)";
    p.appendChild(tag);
  } else if (rp.source === "operator_override") {
    const tag = document.createElement("span");
    tag.className = "work-item-responsible-source";
    tag.textContent = "(assigned)";
    p.appendChild(tag);
  }
  return p;
}

function renderUnthreadedV3(
  runId: string,
  unthreaded: NonNullable<BriefingPayload["unthreaded"]>,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  rerender: () => void,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread unthreaded thread-v3";

  const header = document.createElement("div");
  header.className = "thread-header";
  const h2 = document.createElement("h2");
  h2.textContent = "Cross-cutting";
  header.appendChild(h2);
  section.appendChild(header);

  const globalMode = readCompactnessMode();
  const workItems = unthreaded.work_items ?? [];
  for (const wi of workItems) {
    section.appendChild(
      renderUnthreadedWorkItemV3(runId, wi, fb, corr, globalMode, participantOptions, threadOptions, rerender),
    );
  }
  if (!workItems.length) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "Nothing here this week.";
    header.appendChild(empty);
  }
  return section;
}

function renderUnthreadedWorkItemV3(
  runId: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  globalMode: CompactnessMode,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  rerender: () => void,
): HTMLElement {
  const targetId = unthreadedWorkItemKey(wi.slug);
  const override = readCompactnessOverride(runId, targetId);
  const isCompact = effectiveCompactness(globalMode, override, wi.compact);

  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"} ${isCompact ? "compact" : "expanded"}`;

  const header = document.createElement("div");
  header.className = "work-item-header";
  const h3 = document.createElement("h3");
  h3.textContent = wi.name;
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) h3.appendChild(badge);
  header.appendChild(h3);

  if (wi.summary) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    header.appendChild(p);
  }
  // v3 unthreaded work items: Responsible Party replaces Involved/Key
  // dates per client direction (parallel to renderWorkItemV3 above).
  if (wi.responsible_party) {
    header.appendChild(renderResponsibleParty(wi.responsible_party));
  }
  const wiKinds: string[] = ["responsible_party_assign", "comment", "flag_next_week"];
  if (wi.status === "closed") {
    wiKinds.unshift("work_item_reopen");
  } else {
    wiKinds.unshift("work_item_close");
  }
  const controlsRow = document.createElement("div");
  controlsRow.className = "work-item-controls-row";
  controlsRow.appendChild(
    renderWorkItemCompactnessToggle(runId, targetId, override, isCompact, rerender),
  );
  controlsRow.appendChild(renderFormControls({
    runId,
    targetType: "work_item",
    targetId,
    kinds: wiKinds,
    submitted: fb.get(targetId) ?? [],
    participantOptions,
  }));
  header.appendChild(controlsRow);
  wrapper.appendChild(header);

  if (isCompact && wi.pending?.length) {
    const ul = document.createElement("ul");
    ul.className = "work-item-pending";
    for (const text of wi.pending) {
      const li = document.createElement("li");
      li.textContent = text;
      ul.appendChild(li);
    }
    wrapper.appendChild(ul);
  }

  for (const sec of SECTION_ORDER) {
    const items = wi[sec];
    if (!items?.length) continue;
    const isAlwaysFull = sec === "leadership_attention" ||
      (sec === "action_items" && items.some(isImpactBearingNonStale));
    wrapper.appendChild(
      renderSectionV3(runId, items, sec, (i) =>
        unthreadedWorkItemSectionItemKey(wi.slug, sec, i),
        fb, corr,
        isCompact,
        isAlwaysFull,
        threadOptions,
      ),
    );
  }
  return wrapper;
}

function renderCandidateThreadsV3(
  runId: string,
  candidates: CandidateThreadView[],
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  // Slice 8 Item #1 Layer B — split into structural (promote/merge
  // decision) and relevance_uncertain (confirm-belonging decision)
  // when the agent supplies the `bucket` field; otherwise render all
  // under one heading. The bucket discriminator is plumbed defensively;
  // until the agent emits it, candidates default to the structural
  // bucket and render with the "promote or reject" verb pair.
  const structural = candidates.filter(
    (c) => (c.bucket ?? "structural") === "structural",
  );
  const relevance = candidates.filter((c) => c.bucket === "relevance_uncertain");

  const wrapper = document.createElement("section");
  wrapper.className = "candidates-section";

  if (structural.length) {
    const heading = document.createElement("h3");
    // Decision 9 (close-out): user-facing label switched from
    // "Candidate Threads" to "Suggested Categories". Internal field
    // name (`candidate_threads`) stays for backward compat.
    heading.textContent = relevance.length
      ? "Suggested Categories — promote or reject"
      : "Suggested Categories";
    wrapper.appendChild(heading);
    for (const c of structural) {
      wrapper.appendChild(renderCandidateRow(runId, c, fb, corr));
    }
  }
  if (relevance.length) {
    const heading = document.createElement("h3");
    heading.textContent = "Relevance-uncertain — confirm belonging";
    wrapper.appendChild(heading);
    for (const c of relevance) {
      wrapper.appendChild(renderCandidateRow(runId, c, fb, corr));
    }
  }

  return wrapper;
}

function renderCandidateRow(
  runId: string,
  candidate: CandidateThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "candidate-row";
  const name = document.createElement("div");
  name.className = "candidate-name";
  name.textContent = candidate.name;
  const targetId = threadKey(candidate.slug);
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) name.appendChild(badge);
  row.appendChild(name);
  if (candidate.one_liner) {
    const p = document.createElement("p");
    p.className = "candidate-one-liner";
    p.textContent = candidate.one_liner;
    row.appendChild(p);
  }
  row.appendChild(renderFormControls({
    runId,
    targetType: "thread",
    targetId,
    kinds: ["thread_promote", "thread_reject", "comment"],
    submitted: fb.get(targetId) ?? [],
  }));
  return row;
}

function renderNeedsReview(
  runId: string,
  items: Array<{ item_id: string; summary: string; why_flagged?: string }>,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "needs-review";
  const h2 = document.createElement("h2");
  h2.textContent = "Needs review";
  section.appendChild(h2);
  const ul = document.createElement("ul");
  for (const item of items) {
    const li = document.createElement("li");
    li.className = "row";
    const summary = document.createElement("div");
    summary.className = "row-text";
    summary.textContent = item.summary;
    const targetId = needsReviewItemKey(item.item_id);
    const badge = renderCorrectedBadge(corr.get(targetId));
    if (badge) summary.appendChild(badge);
    li.appendChild(summary);
    if (item.why_flagged) {
      const why = document.createElement("p");
      why.className = "muted";
      why.textContent = item.why_flagged;
      li.appendChild(why);
    }
    li.appendChild(
      renderFormControls({
        runId,
        targetType: "item",
        targetId,
        // Decision 2: review_clarification removed from SPA (operator-only).
        kinds: ["not_relevant", "comment"],
        submitted: fb.get(targetId) ?? [],
      }),
    );
    ul.appendChild(li);
  }
  section.appendChild(ul);
  return section;
}

function renderSection(
  runId: string,
  items: AnyItem[],
  section: Section,
  buildId: (index: number) => string,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = `section section-${section}`;
  const h3 = document.createElement("h3");
  h3.textContent = SECTION_LABEL[section];
  wrapper.appendChild(h3);

  const list = document.createElement("ul");
  items.forEach((item, idx) => {
    const li = document.createElement("li");
    li.className = "row";
    const targetId = buildId(idx);
    li.dataset.targetId = targetId;

    const text = document.createElement("div");
    text.className = "row-text";
    text.textContent = describeItem(item);
    const badge = renderCorrectedBadge(corr.get(targetId));
    if (badge) text.appendChild(badge);
    li.appendChild(text);

    // Slice 7 L3 feedback: verbose 1-3 sentence description below
    // the bold lead. Only renders when synthesizer populated it.
    if (item.description) {
      const desc = document.createElement("p");
      desc.className = "row-description";
      desc.textContent = item.description;
      li.appendChild(desc);
    }

    const meta = describeMeta(item);
    if (meta) {
      const m = document.createElement("p");
      m.className = "muted";
      m.textContent = meta;
      li.appendChild(m);
    }

    li.appendChild(
      renderFormControls({
        runId,
        targetType: "item",
        targetId,
        kinds: kindsForSection(section),
        submitted: fb.get(targetId) ?? [],
      }),
    );

    list.appendChild(li);
  });
  wrapper.appendChild(list);
  return wrapper;
}

export function correctedBadgeTitle(corrections: Correction[]): string {
  // Pure text-formatting separated from DOM creation so it's unit-testable
  // without a DOM environment. The DOM-side `renderCorrectedBadge` builds
  // the span and uses this for the hover tooltip.
  return corrections.map(describeCorrection).join("\n");
}

export function renderCorrectedBadge(
  corrections: Correction[] | undefined,
): HTMLElement | null {
  if (!corrections || corrections.length === 0) return null;
  const span = document.createElement("span");
  span.className = "corrected-badge";
  span.textContent = " ✓ corrected";
  span.title = correctedBadgeTitle(corrections);
  return span;
}

function describeCorrection(c: Correction): string {
  const when = c.recorded_at ? ` (${c.recorded_at.slice(0, 10)})` : "";
  const who = c.recorded_by ? ` by ${c.recorded_by}` : "";
  switch (c.kind) {
    case "operator_note": {
      const subkind = c.kind_note ?? "note";
      const text = c.text ? `: ${c.text}` : "";
      return `${subkind}${text}${who}${when}`;
    }
    case "date_correction":
      return `date_correction · ${c.field ?? "?"} → ${c.new_value ?? "?"}${who}${when}`;
    case "thread_rename":
      return `thread_rename · ${c.from_slug ?? "?"} → ${c.to_slug ?? "?"}${who}${when}`;
    case "thread_merge":
      return `thread_merge · ${(c.source_slugs ?? []).join(", ")} → ${c.target_slug ?? "?"}${who}${when}`;
    case "thread_tag":
      return `thread_tag · ${c.from_thread ?? "?"} → ${c.to_thread ?? "?"}${who}${when}`;
    case "classification":
      return `classification · ${c.item_id ?? "?"}${who}${when}`;
    default:
      return `${c.kind}${who}${when}`;
  }
}

function describeItem(item: AnyItem): string {
  return item.what ?? item.question ?? "(no description)";
}

function describeMeta(item: AnyItem): string {
  const bits: string[] = [];
  if (item.owner) bits.push(`Owner: ${item.owner}`);
  if (item.assignee) bits.push(`Assignee: ${item.assignee}`);
  if (item.decided_by) bits.push(`Decided by: ${item.decided_by}`);
  if (item.decided_at) bits.push(`Decided: ${item.decided_at}`);
  if (item.due) bits.push(`Due: ${item.due}`);
  if (item.status) bits.push(`Status: ${item.status}`);
  if (item.impact) bits.push(`Impact: ${item.impact}`);
  const nextStep = formatRecommendedNextStep(item.recommended_next_step);
  if (nextStep) bits.push(`Next: ${nextStep}`);
  return bits.join(" · ");
}

export function formatRecommendedNextStep(
  v: RecommendedNextStep | string | null | undefined,
): string {
  if (!v) return "";
  if (typeof v === "string") return v;  // Legacy payload shape.
  // Prefer the human attribution (e.g., "Ray Lastra email, 5/4")
  // resolved by the harness. Fall back to the source_ref hash for
  // briefings rendered before the source-label feature landed.
  const sourceLabel = v.attribution ?? v.source_ref;
  const attrib: string[] = [];
  if (sourceLabel) attrib.push(`per ${sourceLabel}`);
  if (v.observed_at) attrib.push(v.observed_at);
  return attrib.length ? `${v.text} (${attrib.join(", ")})` : v.text;
}

// Open-question attribution line. Only prefix "Raised by" when a name is
// present; an unattributed question (raised_by null — the extractor couldn't
// identify an asker) must not render a dangling "Raised by <date>" to the
// client. Returns null when there's nothing to show.
export function openQuestionMeta(
  raisedBy: string | null | undefined,
  raisedAt: string | null | undefined,
): string | null {
  if (raisedBy) {
    const bits = [raisedBy];
    if (raisedAt) bits.push(raisedAt);
    return `Raised by ${bits.join(", ")}`;
  }
  if (raisedAt) return `Raised ${raisedAt}`;
  return null;
}

// N2/#117 — the Q line pairs provenance inline ("asked by X, date"), so the
// question and who asked it stay together. Reuses openQuestionMeta's logic.
export function askedByMeta(
  raisedBy: string | null | undefined,
  raisedAt: string | null | undefined,
): string | null {
  const m = openQuestionMeta(raisedBy, raisedAt);
  return m
    ? m.replace(/^Raised by /, "asked by ").replace(/^Raised /, "asked ")
    : null;
}

// N2/#117 — format a proposed answer's DETERMINISTIC source provenance for the
// citation line: `From "<subject>" — <author>, <date>`, with graceful fallbacks
// (no subject for a transcript/doc source; any field may be absent).
export function proposedSourceLabel(rc: {
  source_subject?: string | null;
  source_author?: string | null;
  source_timestamp?: string | null;
}): string | null {
  const subj = (rc.source_subject ?? "").trim();
  const who = (rc.source_author ?? "").trim();
  const date = formatSourceDate(rc.source_timestamp);
  const whoDate = [who, date].filter(Boolean).join(", ");
  if (subj && whoDate) return `From “${subj}” — ${whoDate}`;
  if (subj) return `From “${subj}”`;
  if (whoDate) return `From ${whoDate}`;
  return null;
}

// Parse a "Mon D" label from an ISO timestamp deterministically — take the date
// parts straight from the string, so no Date() timezone off-by-one.
function formatSourceDate(ts: string | null | undefined): string {
  if (!ts) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ts);
  const mo = m?.[2];
  const day = m?.[3];
  if (!mo || !day) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const name = months[parseInt(mo, 10) - 1];
  if (!name) return "";
  return `${name} ${parseInt(day, 10)}`;
}

export function kindsForSection(section: Section): string[] {
  // Operator-only kinds removed from SPA (Decision 2 close-out):
  //  - thread_retag: redundant with v4 item_move_to_category
  //  - date_correction: field-enum + LLM-mechanics, operator data hygiene
  //  - review_clarification: verdict-enum, LLM-mechanics
  // All three remain in the absorber + CLI for backward compat.
  void section;
  return ["missing_item", "not_relevant", "comment", "flag_next_week"];
}

// --- v4 (Task #9) kind menus -----------------------------------------------
//
// One menu per pencil affordance. Each menu lists the kinds available at
// that level. Mirrors the agent-side contract from Task #8 (see
// agent/corrections.py + agent/feedback_absorb.py). Pencil placement is
// in renderCategoryV4 / renderItemV4 / renderActionV4 / renderUnfiledV4
// below.

export function kindsForCategoryV4(closed: boolean): string[] {
  const lifecycle = closed ? ["thread_reopen"] : ["thread_close"];
  // #38: open Categories only — you don't group a closed Category.
  const grouping = closed ? [] : ["subcategory_add"];
  // #37: re-band a Category into a phase — offered on OPEN and CLOSED alike
  // (dogfood 2026-06-18: a closed Category still renders in its band for the
  // 2-week retention window, so the recipient must be able to file it under the
  // right phase). Pure attribute move; no lifecycle effect.
  const phasing = ["category_move_to_phase"];
  // Operator order 2026-06-12: lifecycle → restructure (rename, merge, phase) →
  // create (sub-category, item) → notes.
  return [
    ...lifecycle,
    "thread_rename", "thread_merge",
    ...phasing,
    ...grouping,
    "item_add",
    // Decision 2: missing_item is the low-friction "something's missing" path
    // at the Category level (lands as OperatorNote for triage); item_add is
    // structured creation when the client is ready to commit slug + name.
    "missing_item",
    "comment", "flag_next_week",
  ];
}

export function kindsForItemV4(
  closed: boolean,
  // Option Y (2026-06-03): an open Item carrying an Item-level
  // closure_candidate ALSO offers "dismiss proposal" (park it open)
  // alongside the always-present "close". Mirrors the Action-level
  // closure_candidate → action_dismiss_closure_candidate visibility.
  hasClosureCandidate: boolean = false,
  // #38: offer "Move to Sub-category" only when the parent Category has ≥1
  // OPEN group (creation never auto-moves; with zero groups the affordance
  // would be a dead end). The caller threads the parent's group list.
  hasOpenSubcategory: boolean = false,
  // #73: offer the reverse "Merge this Item into…" only when ≥1 OTHER open Item
  // exists project-wide (a valid primary to fold into). With none it would be a
  // dead-end picker. The caller threads itemOptions-minus-self.
  hasMergeTarget: boolean = false,
  // Cascade-close (Ray 2026-07-21): when the Item still has OPEN child
  // Actions (all cascade-targetable — the caller checks ai_slugs), the greyed
  // dead-end "Close Item" is REPLACED by the work_item_close_cascade pseudo
  // ("Close Item & its open Actions") — including in the closure_candidate
  // pair, Ray's main flow (approving an agent close proposal on an Item with
  // open children previously dead-ended on the B9 gate).
  cascadeCloseAvailable: boolean = false,
): string[] {
  const closeKind = cascadeCloseAvailable
    ? "work_item_close_cascade"
    : "work_item_close";
  const lifecycle = closed
    ? ["work_item_reopen"]
    : hasClosureCandidate
      ? [closeKind, "work_item_dismiss_closure_candidate"]
      : [closeKind];
  // #66 D4: `work_item_dismiss_merge_candidate` is deliberately NOT offered
  // here. It was, and it dead-lettered 100% of the time: the form's only
  // counterpart value comes from `spec.current.target_work_item_slug`, which is
  // written in exactly one place — the merge-proposal banner's Dismiss handler
  // (forms.ts) — so from the ✎ menu it submitted an empty string. `required`
  // cannot save it (a hidden input is barred from constraint validation), the
  // Worker accepts any payload shape, and the absorber then raises, which
  // dead-letters WITHOUT consuming the event and turns the hourly poll red on
  // every subsequent pull. The recipient saw a green "Submitted".
  //
  // The banner is the working path. Re-adding a menu entry requires a real
  // target PICKER, not a hidden field — until then, do not push this kind.
  // B2 (2026-06-26): "Acknowledge (not tracked)" (seen-but-untracked → Closed
  // Archive's "Acknowledged / Not Tracked" section, never recorded as completed)
  // + "Not relevant" (reject a mis-placed Item → dropped for good, records a
  // `rejected` signal). Open-only. Operator order 2026-06-27: moved out of the
  // lifecycle cluster to sit AFTER Comment, BEFORE Flag next week (see return).
  const disposition = closed
    ? []
    : ["work_item_acknowledge", "work_item_not_relevant"];
  // #58 (PSU = Actions only): Items no longer offer an independent PSU
  // mark. An Item enters the Project Status Update solely by having a
  // marked Action under it (auto-pulled into the snapshot per Decision
  // 5.3). The item_kil_mark/item_kil_unmark wire kinds stay valid for
  // back-compat (historical corrections must still load) but are never
  // surfaced on the Item pencil.
  // Task 3: rename + merge are structural edits on an open Item, grouped
  // with the move affordance. Hidden on closed Items (nothing to restructure).
  // #38 feedback 2.1: when BOTH move destinations exist (open Item + ≥1 open
  // group), ONE combined "Move to…" button (the item_move pseudo-kind, whose
  // tier select resolves to the real wire kind at submit). With a single
  // destination — closed Item, or no open groups — the plain "Move to
  // Category" renders as before (a one-option submenu is just an extra click).
  const move = !closed && hasOpenSubcategory
    ? ["item_move"]
    : ["item_move_to_category"];
  // #73: reverse merge sits right after the forward "Merge Items" — same
  // operation, opposite direction (fold THIS up into a picked primary).
  const structural = closed
    ? []
    : hasMergeTarget
      ? ["work_item_rename", "work_item_merge", "item_merge_into"]
      : ["work_item_rename", "work_item_merge"];
  // Operator 2026-06-28: "Edit narrative" promoted OUT of the rename group to
  // the FIRST menu position (the most-used edit). Open-only (a closed tombstone
  // isn't edited).
  const editNarrative = closed ? [] : ["work_item_set_narrative"];
  // Operator order 2026-06-12: lifecycle → restructure (rename, merge, move)
  // → assign owner → create (add action) → notes. Move sits with the other
  // restructure ops; it's the #38 sub-category move + the move-to-Category path.
  return [
    ...editNarrative,
    ...lifecycle,
    ...structural,
    ...move,
    "responsible_party_assign",
    "action_add",
    // Decision 2: missing_item available at Item level too (e.g., "an
    // Action that should be here isn't"). Lands as OperatorNote.
    "missing_item",
    "comment",
    // Operator order 2026-06-27: disposition verbs (acknowledge / not relevant)
    // sit between Comment and Flag next week.
    ...disposition,
    "flag_next_week",
  ];
}

// #38: the affordances on a sub-category header's pencil. Open groups get
// rename + close + delete + notes; a closed group (rendered as a tombstone
// within its retention window) gets only reopen + comment. Worker-gated:
// subcategory_close 409s when the group still has open member Items (the SPA
// also grays it). #74b: subcategory_delete is NOT gated — it ungroups members
// (no close, no tombstone) — so it's always available on an open group.
export function kindsForSubcategoryV4(closed: boolean): string[] {
  return closed
    ? ["subcategory_reopen", "comment"]
    : [
        "subcategory_rename", "subcategory_close", "subcategory_delete",
        "comment", "flag_next_week",
      ];
}

export function kindsForActionV4(
  hasOpenQuestion: boolean,
  isInvoicesAggregate: boolean,
  kilMarked: boolean,
  priority?: PriorityStatus | null,
  // Decision 12.8: status drives close vs reopen visibility;
  // closure_candidate presence drives dismiss-candidate visibility.
  status?: "open" | "closed" | null,
  hasClosureCandidate: boolean = false,
  // B1 / #73: offer the reverse "Merge this Action into…" only when ≥1 OTHER
  // open Action exists project-wide (a valid primary to fold into). With none it
  // would be a dead-end picker. The caller threads actionOptions-minus-self.
  hasMergeTarget: boolean = false,
): string[] {
  // Same conditional pattern as kindsForItemV4 — show only the
  // relevant toggle direction.
  // B5 (2026-06-23, Ray): "Add open question" removed from the Action menu —
  // a question is just an Action (client: "the question is an action"); the
  // affordance added clutter with no use. Kind kept in the enum for back-compat
  // (historical corrections still load); only the offer is withdrawn.
  //
  // Client 2026-08-12: "Resolve open question" is gone from here too, and its
  // "which question" picker with it. Every question disposition now lives on a
  // per-question ✎ rendered beside the question itself (see the Open Questions
  // block below). The picker was real friction, not a rounding error — 14 of 45
  // live Actions carry more than one question and one carries five, so answering
  // meant opening this menu and re-identifying, in a dropdown, a question that
  // was already on screen.
  const questions: string[] = [];
  const invoiceOps = isInvoicesAggregate
    ? ["invoice_add", "invoice_status_change", "invoice_blocking_note_set"]
    : [];
  const kil = kilMarked ? ["action_kil_unmark"] : ["action_kil_mark"];

  // Decision 3A (close-out 2026-05-25): priority-aware metadata
  // edits. The per-priority required-fields map mirrors the
  // synthesizer prompt rules (see synthesizer.md priority guard) +
  // Pydantic constraints on Action. Active actions have no reason /
  // impact / off_hold_condition; On Hold has no due. Hiding the
  // irrelevant kinds keeps the popover scoped to what the client
  // can usefully edit at the current priority.
  const metaByPriority: Record<string, readonly string[]> = {
    "Active": ["action_set_due"],
    "Critical": [
      "action_set_reason", "action_set_impact", "action_set_due",
    ],
    "Delay Potential": [
      "action_set_reason", "action_set_impact", "action_set_due",
    ],
    "On Hold": ["action_set_reason", "action_set_off_hold_condition"],
  };
  // Default to "all four" when priority is unknown/missing — the
  // pre-Decision-3A behavior. Defensive against missing payload data.
  const metaFields = priority
    ? metaByPriority[priority] ?? [
        "action_set_reason", "action_set_impact",
        "action_set_off_hold_condition", "action_set_due",
      ]
    : [
        "action_set_reason", "action_set_impact",
        "action_set_off_hold_condition", "action_set_due",
      ];
  // Round-4 #2 (2026-06-12): ≥2 per-field edit buttons flatten into ONE
  // "Edit details" pseudo-kind (decomposed back into the per-field wire
  // kinds at submit — forms.ts collectActionDetailEdits). A single edit
  // field (Active → due) keeps its direct button: no one-option nesting,
  // the same rule as the combined Move.
  const metaKinds = metaFields.length > 1 ? ["action_edit_details"] : metaFields;

  // Decision 12.8: lifecycle kinds.
  // - Open Action: always show action_close. If a closure_candidate
  //   exists, ALSO show action_dismiss_closure_candidate.
  // - Closed Action: show action_reopen.
  const lifecycle: string[] = [];
  if (status === "closed") {
    lifecycle.push("action_reopen");
  } else {
    lifecycle.push("action_close");
    if (hasClosureCandidate) {
      lifecycle.push("action_dismiss_closure_candidate");
    }
  }
  // B2 (2026-06-26): "Acknowledge (not tracked)" (seen-but-untracked → Closed
  // Archive, never completed) + "Not relevant" (reject a mis-placed Action →
  // dropped for good, records a `rejected` signal). Open-only. Operator order
  // 2026-06-27: moved out of the lifecycle cluster to sit AFTER Comment, BEFORE
  // Flag next week (see return).
  const disposition = status === "closed"
    ? []
    : ["action_acknowledge", "action_not_relevant"];
  // Open improvement #3 / B6 / B8: rename + merge + move-to-Item are open-only
  // (a closed Action is a retention tombstone). Operator order 2026-06-12 wants
  // rename+merge grouped near the top (with the Category/Item restructure
  // cluster) and move-to-Item lower, after the metadata edits — so they're
  // split into two groups rather than one `structural`.
  // B1 / #73: the reverse merge sits right after the forward "Merge Actions" —
  // same operation, opposite direction (fold THIS up into a picked primary).
  // Mirrors the Item tier's rename → merge → merge_into order.
  const renameMerge = status === "closed"
    ? []
    : hasMergeTarget
      ? ["action_rename", "action_merge", "action_merge_into"]
      : ["action_rename", "action_merge"];
  // Operator 2026-06-28: "Edit narrative" promoted OUT of the rename group to
  // the FIRST menu position (it's the most-used edit). Open-only — a closed
  // tombstone isn't edited.
  const editNarrative = status === "closed" ? [] : ["action_set_narrative"];
  // #76: promote-to-Item sits with move-to-Item (both relocate the Action).
  // Open-only. Offered on every open Action; Ray expects to use it rarely (on
  // agent-assumed rows) — gating to an is_new/assumed signal is a future
  // refinement (this fn doesn't receive one).
  const moveToItem = status === "closed"
    ? []
    : ["action_move_to_item", "action_to_item"];

  // Operator order 2026-06-12: lifecycle → rename, merge → Mark PSU → change
  // priority → edit details → move to Item → questions → (invoices, on the
  // aggregate Action) → notes.
  return [
    ...editNarrative,
    ...lifecycle,
    ...renameMerge,
    ...kil,
    "action_priority_change",
    ...metaKinds,
    ...moveToItem,
    ...questions,
    ...invoiceOps,
    "comment",
    // Operator order 2026-06-27: disposition verbs (acknowledge / not relevant)
    // sit between Comment and Flag next week.
    ...disposition,
    "flag_next_week",
  ];
}

export function kindsForNeedsInputV4(): string[] {
  // #60 PR4-v2 (2026-06-11 review): the row's placement surface is TWO verbs —
  // "Place as New" (a tier select; an "already complete" checkbox makes it
  // born-closed) and "Merge Into Existing" (fold into an existing entity) —
  // plus the dispositions. Supersedes the four-button A³/A² set (3 Place +
  // File). Place-as-New is a pseudo-kind resolved by tier at submit.
  return [
    "place_new", "merge_into_existing",
    "informational_acknowledge", "not_relevant", "flag_next_week", "comment",
  ];
}

// ===========================================================================
// v4 render path — Category → Item → Action redesign.
//
// Entry point is `renderBriefingV4`, dispatched from `renderBriefingViewer`
// when payload_schema_version === 4. Parallel to the v3 path; doesn't
// reuse v3 helpers because v4's shape (Unfiled top-level, explicit
// path numbers, priority enum, Open Questions sub-section) is too
// different for additive overlays. Plan doc:
// ~/.claude/plans/category-item-action-redesign-2026-05-22.md.
//
// Pencil affordances are placeholder click targets in this PR — the
// actual new correction kinds (action_priority_change,
// action_open_question_add, item_kil_mark, place_from_unfiled, etc.)
// land in Tasks #8 (agent) + #9 (SPA forms). v3 form scaffolding
// (thread_retag, missing_item, comment, flag_next_week) IS wired
// where the encoding is unchanged.
// ===========================================================================

// Exported for unit testing — kept const, mutation by callers
// would corrupt the priority sort order so freeze defensively.
export const PRIORITY_ORDER: readonly PriorityStatus[] = Object.freeze([
  "Critical", "Delay Potential", "Active", "On Hold", "Informational",
]);

/** Map a priority_status enum value to its CSS modifier class. Falls
 * back to "active" for missing/invalid values so the renderer never
 * emits a class-less row (which would leave the left-edge color
 * unset). Exported for unit test coverage. */
export function priorityClass(p: PriorityStatus | undefined | null): string {
  switch (p) {
    case "Critical": return "critical";
    case "Delay Potential": return "delay";
    case "On Hold": return "hold";
    case "Informational": return "informational";
    default: return "active";
  }
}

// #59 (client feedback 2026-06-09): the "Due …" meta bit for an Action.
// Returns `Due <date>` when a due is set, `Due date: TBD` when an OPEN Action
// has none (a missing date stays visible and prompts a look — a blank field
// gets ignored), or null when no Due bit should show. On Hold + Informational
// carry no due by contract; a closed Action shows `Closed …` elsewhere, so no
// TBD nudge on a tombstone. Shared by the briefing tree (renderAction) and the
// PSU (kil_marks.renderActionRow) so the two never drift.
export function dueMetaBit(
  priority: string | null | undefined,
  due: string | null | undefined,
  status: string | null | undefined,
): string | null {
  if (priority === "On Hold" || priority === "Informational") return null;
  if (due) return `Due ${due}`;
  if (status === "closed") return null;
  return "Due date: TBD";
}

// A² (2026-06-03): NEW-this-period pill. `is_new` is set deterministically
// by the agent (mark_new). Globally suppressible via localStorage
// `spa.showNewBadge=false` — the one-flag "easy disable" if the briefing
// reads badge-heavy (no code change / redeploy needed).
function newBadgeEnabled(): boolean {
  try {
    return localStorage.getItem("spa.showNewBadge") !== "false";
  } catch {
    return true;
  }
}

function appendNewPill(parent: HTMLElement, isNew?: boolean | null): void {
  if (!isNew || !newBadgeEnabled()) return;
  parent.appendChild(document.createTextNode(" "));
  const pill = document.createElement("span");
  pill.className = "new-pill";
  pill.textContent = "NEW";
  pill.setAttribute("title", "New this period");
  parent.appendChild(pill);
}

// A1 (2026-07-22): "UPDATED" pill — a carryover Item/Action the narrative-
// merger judged a MERGE this period (real new info), distinct from NEW (brand
// new). Independently suppressible via localStorage `spa.showUpdatedBadge=false`
// — mirrors the NEW badge's one-flag easy-disable (no redeploy).
function updatedBadgeEnabled(): boolean {
  try {
    return localStorage.getItem("spa.showUpdatedBadge") !== "false";
  } catch {
    return true;
  }
}

function appendUpdatedPill(
  parent: HTMLElement, isUpdated?: boolean | null,
): void {
  if (!isUpdated || !updatedBadgeEnabled()) return;
  parent.appendChild(document.createTextNode(" "));
  const pill = document.createElement("span");
  pill.className = "updated-pill";
  pill.textContent = "UPDATED";
  pill.setAttribute("title", "Updated this period");
  parent.appendChild(pill);
}

// A1 (merge optimistic UI): a merge closes its sources, but their context
// folds forward into the target — distinct from a plain close. closed_reason
// "merged into <target>" routes them to a "Recently Merged → into X"
// treatment so the recipient sees the merge took AND where it went.
function isMergedClosure(reason?: string | null): boolean {
  return !!reason && /merged\s+into/i.test(reason);
}
function mergeTargetFromReason(reason?: string | null): string | null {
  const m = (reason ?? "").match(/merged\s+into\s+(.+)$/i);
  return m?.[1]?.trim() ?? null;
}

// #37: a phase band banner. Deliberately a <div>, NOT a heading element —
// mirrors the j2's "bold standalone line, no new heading level" rule (#38
// precedent) so the document outline, TOC, and docx export stay anchored on
// `## <N>. Category`. data-phase drives the CSS accent; the anchor id lets
// the TOC scroll to a band.
function renderPhaseBandHeader(phase: PhaseValue): HTMLElement {
  const band = document.createElement("div");
  band.className = "phase-band-header";
  band.dataset.phase = phase;
  band.id = phaseAnchorId(phase);
  band.textContent = PHASE_LABELS[phase];
  return band;
}

function renderBriefingV4(
  el: HTMLElement,
  record: BriefingRecord,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  _corr: Map<string, Correction[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
): void {
  // Compactness now flows per-work-item via `effectiveCompactness(
  //   globalMode, perItemOverride, synth.compact)`. No briefing-wide
  // class — each work-item renders with `.work-item-compact` only
  // when its effective state says so. This way a per-item override
  // to expanded wins over a global Compact mode, matching v3.

  // Compactness toggle + Add Category pencil share one row: View
  // buttons on the left, pencil on the right, with padding above and
  // below the row. Earlier we split them into two rows for v3 parity
  // on the toggle line; operator reverted that 2026-05-24 because
  // the orphan pencil row looked disconnected.
  const toggleRow = document.createElement("div");
  toggleRow.className = "v4-toggle-row";
  toggleRow.appendChild(renderCompactnessToggle(() => {
    renderBriefingViewer(el, record, fb);
  }));

  // A² (2026-06-03): "New only" filter — composable with the density
  // toggle (NOT a 4th density mode): density = how much detail, filter =
  // which Items. Shows only Items new this period (or carrying a new
  // Action). Persisted across re-renders; applied as a container class
  // consumed by CSS (instant, no re-render on toggle).
  const filterNewOnly = (() => {
    try { return localStorage.getItem("spa.filterNewOnly") === "true"; }
    catch { return false; }
  })();
  if (filterNewOnly) el.classList.add("filter-new-only");
  const filterChip = document.createElement("label");
  filterChip.className = "new-filter-chip";
  const filterCb = document.createElement("input");
  filterCb.type = "checkbox";
  filterCb.checked = filterNewOnly;
  filterCb.addEventListener("change", () => {
    try { localStorage.setItem("spa.filterNewOnly", String(filterCb.checked)); }
    catch { /* private mode — filter still applies for this view */ }
    el.classList.toggle("filter-new-only", filterCb.checked);
  });
  filterChip.appendChild(filterCb);
  filterChip.appendChild(document.createTextNode(" Show New only"));
  toggleRow.appendChild(filterChip);

  // A1 (2026-07-22): combined "new/updated" filter — shows Items new this
  // period OR carrying real new info this week (a narrative-merger MERGE), so a
  // recipient sees everything that changed in one view. Deliberately a SEPARATE
  // chip from "New only" (which is a stricter subset), NOT a second independent
  // "Updated only": two mutually-exclusive "only" filters would AND together via
  // the container-class CSS and hide everything. When both chips are checked the
  // new-only rule wins (hides non-new even if updated) — no empty set, since the
  // changed predicate is a superset of new. Same instant class/CSS mechanism.
  const filterChangedOnly = (() => {
    try { return localStorage.getItem("spa.filterChangedOnly") === "true"; }
    catch { return false; }
  })();
  if (filterChangedOnly) el.classList.add("filter-changed-only");
  const changedChip = document.createElement("label");
  changedChip.className = "new-filter-chip";
  const changedCb = document.createElement("input");
  changedCb.type = "checkbox";
  changedCb.checked = filterChangedOnly;
  changedCb.addEventListener("change", () => {
    try {
      localStorage.setItem("spa.filterChangedOnly", String(changedCb.checked));
    } catch { /* private mode — filter still applies for this view */ }
    el.classList.toggle("filter-changed-only", changedCb.checked);
  });
  changedChip.appendChild(changedCb);
  changedChip.appendChild(document.createTextNode(" Show new/updated"));
  toggleRow.appendChild(changedChip);

  const categoryAddTargetId = `project:${record.project_slug}`;
  const addCategoryControls = renderFormControls({
    runId: record.run_id,
    targetType: "project",
    targetId: categoryAddTargetId,
    kinds: ["category_add"],
    // Filter to THIS control's kind: the Edit-Summary pencil (R2-3 fast-follow)
    // shares the same `project:<slug>` targetId, so an unfiltered list would
    // cross-show set_executive_summary chips here.
    submitted: (fb.get(categoryAddTargetId) ?? []).filter(
      (e) => e.kind === "category_add",
    ),
    participantOptions,
    threadOptions,
  });
  addCategoryControls.classList.add("v4-add-category-control");
  toggleRow.appendChild(addCategoryControls);
  el.appendChild(toggleRow);

  // Decision 5 (close-out): Unfiled and Suggested Categories both
  // render BELOW the Category tree, grouped under a single "Needs
  // Your Input" wrapper. Was: Unfiled top with ⚠ alarm, Candidate
  // Threads bottom. Spec is silent on placement; main project status
  // reads first; LLM-uncertainty surfaces grouped together at end.
  // Wrapper appended after the Categories loop further below.

  // Sort Categories by display_number ASC so the render order matches
  // the path-notation hierarchy (1, 2, 3, ...). Synth doesn't guarantee
  // payload order — it tends to emit threads in first-seen / carryover
  // order, which produced visual orderings like [..., 5, 14, 6, ...]
  // when display_number was assigned at upsert time in a different
  // sequence than the current emit. Numeric-aware comparator; entries
  // with null / non-numeric display_number sort to the end.
  const sortedThreads = [...(record.payload.threads ?? [])].sort((a, b) => {
    const aNum = a.display_number != null ? parseInt(a.display_number, 10) : NaN;
    const bNum = b.display_number != null ? parseInt(b.display_number, 10) : NaN;
    if (Number.isNaN(aNum) && Number.isNaN(bNum)) return 0;
    if (Number.isNaN(aNum)) return 1;
    if (Number.isNaN(bNum)) return -1;
    return aNum - bNum;
  });
  // Task 3: project-wide open-Item options for the work_item_merge
  // multi-select (Ray picks which sibling Items to fold in). Collected
  // once from the full payload, like the Unfiled place picker below.
  const allItemOptions: ItemOption[] = [];
  // B8 cross-Item action_merge: project-wide open Actions for the merge
  // picker — each carries its full target id + parent Item label so the
  // submit handler can re-parent a cross-Item source before merging.
  const allActionOptions: ActionOption[] = [];
  for (const t of record.payload.threads ?? []) {
    for (const wi of t.work_items ?? []) {
      if (wi.status === "closed") continue;
      allItemOptions.push({
        slug: wi.slug,
        name: wi.name,
        display_number: wi.display_number ?? null,
        thread_name: t.name,
      });
      const itemLabel =
        `${wi.display_number ? wi.display_number + " " : ""}${wi.name}`;
      for (const a of wi.action_items ?? []) {
        if ((a.status ?? "open") !== "open" || !a.ai_slug) continue;
        allActionOptions.push({
          ai_slug: a.ai_slug,
          text: a.what ?? a.text ?? "",
          display_number: a.display_number ?? null,
          target_id: actionItemKey(t.slug, wi.slug, a.ai_slug),
          work_item_slug: wi.slug,
          item_label: itemLabel,
        });
      }
    }
  }
  // #63 follow-up: order the move-target Item picker by Item number (was
  // payload order, which looked unsorted — e.g. 2.9 before 2.1).
  allItemOptions.sort((a, b) => compareDisplayNumber(a.display_number, b.display_number));
  // #37: partition the display_number-sorted Categories into the fixed 4
  // phase bands, with an always-present banner above each (delta #1,
  // 2026-06-18: render all 4 bands even when empty — the client wants the
  // phase structure visible at all times; pruning is an explicit SPA close,
  // never an auto-hide. Overrides the v5 §9 activation rule). The inner
  // filter preserves the numeric Category sort within each band; null/legacy
  // phase falls into General. The Needs-Your-Input + Needs-Review tail below
  // renders after the (last) General band, matching today's global-tail spot.
  for (const phase of PHASE_ORDER) {
    el.appendChild(renderPhaseBandHeader(phase));
    for (const thread of sortedThreads) {
      if (phaseBand(thread.phase) !== phase) continue;
      el.appendChild(renderCategoryV4(
        record.run_id, thread, fb, participantOptions, threadOptions,
        allItemOptions, allActionOptions,
      ));
    }
  }

  // #60 (2026-06-09 review): ONE undifferentiated "Needs Your Input" queue.
  // Supersedes the 2026-06-03 three-sub-section layout (Suggested Categories /
  // Unfiled / Informational): Ray asked for "all one area" with no
  // category-vs-informational pre-framing. candidate_threads[] is NO LONGER a
  // recipient surface (option (a)) — the #61 substantiation gate + synth
  // rule 1c route a candidate's content to unfiled[] with missing-info
  // framing, so the bare topic row would be redundant noise; the array stays
  // in the payload as harness telemetry only.
  const hasUnfiled = !!record.payload.unfiled?.length;
  const hasInformational = !!record.payload.informational?.length
    || !!record.payload.informational_acknowledged?.length;
  if (hasUnfiled || hasInformational) {
    // Decision 7A / A³: OPEN Items across all Categories for the Place-as-New
    // → Action parent picker (open-only — placing under a closed Item would be
    // a contradiction; that's what "Already complete" is for).
    const itemOptions: ItemOption[] = [];
    // #60 PR4-v2 Merge-Into-Existing pickers — OPEN+CLOSED per tier (a row may
    // dedup against either). Separate from the open Place pickers, which keep
    // excluding closed/retired per the 2026-06-09 client decision.
    const mergeThreadOptions: ThreadOption[] = [];
    const mergeItemOptions: ItemOption[] = [];
    const mergeActionOptions: ClosedActionOption[] = [];
    for (const t of record.payload.threads ?? []) {
      mergeThreadOptions.push({
        slug: t.slug, name: t.name, display_number: t.display_number ?? null,
        closed: t.status === "closed",
      });
      for (const wi of t.work_items ?? []) {
        const wiClosed = wi.status === "closed";
        const itemOpt: ItemOption = {
          slug: wi.slug, name: wi.name,
          display_number: wi.display_number ?? null, thread_name: t.name,
          closed: wiClosed,
        };
        mergeItemOptions.push(itemOpt);
        if (!wiClosed) itemOptions.push(itemOpt);
        for (const ai of wi.action_items ?? []) {
          if (!ai.ai_slug) continue;
          // Synth emits `what` (alias); persisted payloads use `text`.
          const aiText = ai.text ?? ai.what ?? "";
          if (!aiText) continue;
          // #2.2: lead with the Action's display number (N.N.N) when present.
          const num = ai.display_number ? `${ai.display_number} ` : "";
          mergeActionOptions.push({
            work_item_slug: wi.slug,
            action_item_slug: ai.ai_slug,
            label: `${num}${aiText} (${wi.name})`,
            closed: ai.status === "closed",
          });
        }
      }
    }
    itemOptions.sort((a, b) => compareDisplayNumber(a.display_number, b.display_number));
    mergeItemOptions.sort((a, b) => compareDisplayNumber(a.display_number, b.display_number));
    // Closed Items beyond the blob's 2-week retention come from the
    // Closed-Items snapshot (all ages — the kimley-horn case). Async: the
    // arrays are captured by reference in each row's form spec, and
    // buildForm reads them lazily at popover-open, so options that arrive
    // before the recipient opens the form (near-always) just appear.
    // Memoized — re-renders (KIL toggles, feedback events) reuse the
    // session's fetch. Categories/Actions stay blob-only in v1.
    closedItemsSnapshotOnce().then((snap) => {
      const have = new Set(mergeItemOptions.map((i) => i.slug));
      for (const ci of snap.closed_items ?? []) {
        if (have.has(ci.slug)) continue;
        mergeItemOptions.push({
          slug: ci.slug, name: ci.name,
          display_number: ci.display_number ?? null,
          thread_name: ci.thread_name ?? undefined,
          closed: true,
        });
      }
      mergeItemOptions.sort(
        (a, b) => compareDisplayNumber(a.display_number, b.display_number),
      );
    }).catch(() => { /* picker degrades to blob-only closed targets */ });

    el.appendChild(renderNeedsInputUnifiedV4(
      record.run_id,
      record.payload.unfiled ?? [],
      record.payload.informational ?? [],
      record.payload.informational_acknowledged ?? [],
      fb, participantOptions, threadOptions, itemOptions,
      { mergeThreadOptions, mergeItemOptions, mergeActionOptions },
      record.payload.merge_proposals ?? [],
    ));
  }

  // v3 legacy `needs_review` surface. E1 (Ray, 2026-07-14): this block leaked
  // non-project items (the queue is surfaced run-wide, unfiltered by project —
  // agent/state.py iter_review_queue) AND is a read-only dead-end with no
  // recipient affordance. Operator decision: OPERATOR-ONLY for now — recipients
  // only see actionable work. Render ONLY on the operator's /preview surface so
  // it drops off the live recipient briefing entirely. Backlog: scope the queue
  // by project + an actionable "confirm this is a [Project] item" hybrid.
  if (inPreviewMode() && record.payload.needs_review?.length) {
    el.appendChild(
      renderNeedsReviewV4(record.run_id, record.payload.needs_review, fb),
    );
  }
}

// #60 (2026-06-09 review): one undifferentiated Needs-Your-Input queue.
// unfiled[] + informational[] interleave into a single type-neutral list —
// no "Suggested Category" / "FYI" / "Unfiled" sub-headings or chips (Ray:
// "all one area", "let me decide"). Every row gets the SAME full affordance
// set (kindsForNeedsInputV4). Target identity stays per-source
// (unfiled:<id> / informational:<id>) so prior feedback decoration, Worker
// routing, and the absorber are untouched (architecture option A).
type NeedsInputEntryView =
  | { source: "unfiled"; e: UnfiledItemView }
  | { source: "informational"; e: InformationalItemView };

function renderNeedsInputUnifiedV4(
  runId: string,
  unfiled: UnfiledItemView[],
  informational: InformationalItemView[],
  acknowledged: InformationalItemView[],
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  itemOptions: ItemOption[],
  merge: {
    mergeThreadOptions: ThreadOption[];
    mergeItemOptions: ItemOption[];
    mergeActionOptions: ClosedActionOption[];
  },
  mergeProposals: MergeProposalView[] = [],
): HTMLElement {
  const section = document.createElement("section");
  // Class pairing: `.unfiled-section` carries the card chrome (bg/border/
  // row dividers), `.needs-input-section` the anchor offset + h2 type.
  section.className = "unfiled-section needs-input-section";
  // Decision 12.15 (2026-05-28): stable id for the sidebar TOC link.
  section.id = NEEDS_INPUT_ANCHOR_ID;

  const active = unfiled.length + informational.length + mergeProposals.length;
  const h2 = document.createElement("h2");
  h2.textContent = `Needs Your Input (${active})`;
  section.appendChild(h2);

  const lead = document.createElement("p");
  lead.className = "lead";
  // Honest copy per state: the call-to-action only when there's something
  // to act on (the section can render with 0 active entries when only the
  // acknowledged collapse remains).
  lead.textContent = active
    ? "Things the agent couldn't place or decide on its own — you decide " +
      "what each is. Place it as a Category, Item, or Action; file it under " +
      "already-closed work; merge a possible duplicate into the Item it " +
      "matches; dismiss it (kept on the record); or mark it not relevant."
    : "Nothing needs your input this week.";
  section.appendChild(lead);

  // Type-neutral interleave: NEW first, then most recent source activity.
  const entries: NeedsInputEntryView[] = [
    ...unfiled.map((e) => ({ source: "unfiled" as const, e })),
    ...informational.map((e) => ({ source: "informational" as const, e })),
  ];
  entries.sort((a, b) => {
    const newDiff = Number(!!b.e.is_new) - Number(!!a.e.is_new);
    if (newDiff) return newDiff;
    return (b.e.source_timestamp ?? "").localeCompare(a.e.source_timestamp ?? "");
  });

  const fillRow = (
    li: HTMLElement, e: UnfiledItemView | InformationalItemView,
    // The acknowledged collapse is a read-only record — triage PROMPTS
    // (Hint / Missing) would be a call-to-action on a row with no controls.
    opts: { triagePrompts: boolean } = { triagePrompts: true },
  ): void => {
    const text = document.createElement("strong");
    text.textContent = e.text;
    li.appendChild(text);
    appendNewPill(li, e.is_new);
    if (e.attribution) {
      const attr = document.createElement("span");
      attr.className = "muted";
      attr.textContent = ` (${e.attribution})`;
      li.appendChild(attr);
    }
    // Origin breadcrumb (guard-relocated entries) — provenance context,
    // not a type label.
    if ("origin_thread_name" in e && e.origin_thread_name) {
      const origin = document.createElement("div");
      origin.className = "muted";
      origin.style.fontStyle = "italic";
      origin.style.marginTop = "2px";
      origin.textContent = e.origin_work_item_name
        ? `from ${e.origin_thread_name} › ${e.origin_work_item_name}`
        : `from ${e.origin_thread_name}`;
      li.appendChild(origin);
    }
    if (e.narrative) {
      const narrative = document.createElement("div");
      narrative.className = "muted";
      narrative.style.marginTop = "2px";
      narrative.textContent = e.narrative;
      li.appendChild(narrative);
    }
    if (opts.triagePrompts && "hint" in e && e.hint) {
      const hint = document.createElement("div");
      hint.className = "muted";
      hint.style.fontStyle = "italic";
      hint.style.marginTop = "2px";
      hint.textContent = `Hint: ${e.hint}`;
      li.appendChild(hint);
    }
    // #61: "you're missing information" line — what would settle the
    // placement, plus the agent's best tier guess. Replaces the old
    // assume-it's-a-Category framing.
    if (opts.triagePrompts && "missing" in e && e.missing?.length) {
      const missing = document.createElement("div");
      missing.className = "muted needs-input-missing";
      missing.style.marginTop = "2px";
      const tierLead = ("suggested_tier" in e && e.suggested_tier)
        ? (e.suggested_tier === "action" ? "Likely an Action — " : "Likely an Item — ")
        : "";
      missing.textContent = `${tierLead}Missing: ${e.missing.join(" · ")}`;
      li.appendChild(missing);
    }
  };

  const ul = document.createElement("ul");

  // #66 D4: proposed duplicates render as ROWS IN THIS SAME LIST, not under a
  // sub-heading. #60 (2026-06-09 client review) made this ONE undifferentiated
  // queue "with no type sub-headings", superseding a three-bucket layout — a
  // "Possible duplicates" section reintroduced exactly what that removed.
  const proposalSide = (
    li: HTMLElement, label: string, side: MergeProposalSideView,
  ): void => {
    const row = document.createElement("div");
    row.className = "muted";
    row.style.marginTop = "2px";
    const num = side.display_number ? `${side.display_number} ` : "";
    const bits = [`${label}: ${num}${side.name ?? side.slug}`];
    if (side.category) bits.push(side.category);
    bits.push(`${side.action_count ?? 0} action${side.action_count === 1 ? "" : "s"}`);
    row.textContent = bits.join(" · ");
    li.appendChild(row);
    if (side.narrative) {
      const n = document.createElement("div");
      n.className = "muted";
      n.style.marginLeft = "12px";
      n.textContent = side.narrative;
      li.appendChild(n);
    }
  };

  for (const prop of mergeProposals) {
    const li = document.createElement("li");
    li.className = "unfiled-row";

    const lead = document.createElement("strong");
    const intoNum = prop.into.display_number ? `${prop.into.display_number} ` : "";
    lead.textContent =
      `${prop.fold.name || prop.fold.slug} — possibly the same as ` +
      `${intoNum}${prop.into.name || prop.into.slug}`;
    li.appendChild(lead);

    // BOTH sides in full. The row must be able to produce a NO: merging is the
    // destructive option (retires an Item, re-parents its Actions) AND teaches
    // the matter ledger a durable alias, at ~27% measured precision.
    proposalSide(li, "This", prop.fold);
    proposalSide(li, "Existing", prop.into);

    if (prop.rationale) {
      const why = document.createElement("div");
      why.className = "muted";
      why.style.fontStyle = "italic";
      why.style.marginTop = "2px";
      // Attributed, so it reads as one input rather than the framing.
      why.textContent = `Agent's reasoning: ${prop.rationale}`;
      li.appendChild(why);
    }

    const targetId = prop.fold.thread
      ? `thread:${prop.fold.thread}/work_item:${prop.fold.slug}`
      : `unthreaded/work_item:${prop.fold.slug}`;
    const controls = renderFormControls({
      runId,
      targetType: "work_item",
      targetId,
      kinds: ["item_merge_into", "work_item_dismiss_merge_candidate"],
      submitted: fb.get(targetId) ?? [],
      participantOptions,
      threadOptions,
      itemOptions,
      current: {
        merge_primary_item: prop.into.slug,
        target_work_item_slug: prop.into.slug,
        from_proposal: prop.id,
      },
      mergeThreadOptions: merge.mergeThreadOptions,
      mergeItemOptions: merge.mergeItemOptions,
      mergeActionOptions: merge.mergeActionOptions,
    });
    controls.classList.add("unfiled-controls");
    li.appendChild(controls);
    ul.appendChild(li);
  }

  for (const entry of entries) {
    const li = document.createElement("li");
    li.className = "unfiled-row";
    fillRow(li, entry.e);
    const targetId = entry.source === "unfiled"
      ? unfiledKey(entry.e.id)
      : informationalKey(entry.e.id);
    const controls = renderFormControls({
      runId,
      targetType: entry.source,
      targetId,
      kinds: kindsForNeedsInputV4(),
      submitted: fb.get(targetId) ?? [],
      participantOptions,
      threadOptions,
      itemOptions,
      // The Place tiers + dispositions need the entry's source_ref (bucket
      // suppression) + id; File-under-Closed also snapshots the text as the
      // record's body. Injected at submit — not visible fields.
      bucketContext: {
        sourceRef: entry.e.source_ref, entryId: entry.e.id,
        entryText: entry.e.text,
        entryNarrative: entry.e.narrative ?? undefined,
        // #1.4: candidate-folded rows carry candidate_slug so a Place/Merge
        // retires the inferred thread.
        candidateSlug: entry.source === "unfiled"
          ? entry.e.candidate_slug ?? undefined : undefined,
      },
      mergeThreadOptions: merge.mergeThreadOptions,
      mergeItemOptions: merge.mergeItemOptions,
      mergeActionOptions: merge.mergeActionOptions,
    });
    controls.classList.add("unfiled-controls");
    li.appendChild(controls);
    ul.appendChild(li);
  }
  section.appendChild(ul);

  // Collapsed "Acknowledged this period (N)" — visible-but-tucked record of
  // dismissed-as-acknowledged entries (read-only; they age out naturally).
  // Holds acknowledged unfiled entries too (#60 widened the kind).
  if (acknowledged.length) {
    const details = document.createElement("details");
    details.className = "informational-acknowledged";
    const summary = document.createElement("summary");
    summary.textContent = `Acknowledged this period (${acknowledged.length})`;
    details.appendChild(summary);
    const ackUl = document.createElement("ul");
    for (const e of acknowledged) {
      const li = document.createElement("li");
      li.className = "unfiled-row acknowledged";
      fillRow(li, e, { triagePrompts: false });
      if (e.acknowledged_by) {
        const by = document.createElement("span");
        by.className = "muted";
        by.textContent = ` — acknowledged by ${e.acknowledged_by}`;
        li.appendChild(by);
      }
      ackUl.appendChild(li);
    }
    details.appendChild(ackUl);
    section.appendChild(details);
  }
  return section;
}


function renderCategoryV4(
  runId: string,
  thread: ThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  itemOptions: ItemOption[],
  // B8 cross-Item action_merge: project-wide open Actions for the merge
  // picker (so it can fold an Action from another Item, not just siblings).
  allActionOptions: ActionOption[] = [],
): HTMLElement {
  const section = document.createElement("section");
  // v4-tagged so styles.css's `.thread.thread-v4 > .closed-items-divider`
  // and any future v4-only thread chrome can hook in without crossing
  // the v3 selectors.
  section.className = "thread thread-v4";
  if (thread.status === "closed") section.classList.add("closed");

  const header = document.createElement("div");
  header.className = "thread-header";
  // Decision 12.11 (2026-05-28, client feedback #8): TOC anchor id on
  // each Category header so the sidebar TOC can scroll to it. Format
  // matches `categoryAnchorId(slug)` below so renderTocV4 builds the
  // same href.
  if (thread.slug) {
    header.id = categoryAnchorId(thread.slug);
  }

  const h2 = document.createElement("h2");
  if (thread.display_number) {
    const num = document.createElement("span");
    num.className = "path-num";
    num.textContent = `${thread.display_number}.`;
    h2.appendChild(num);
  }
  h2.appendChild(document.createTextNode(thread.name));
  if (thread.status === "closed") {
    const pill = document.createElement("span");
    pill.className = "status-pill closed";
    pill.textContent = thread.closed_at
      ? `Closed · ${thread.closed_at}`
      : "Closed";
    h2.appendChild(document.createTextNode(" "));
    h2.appendChild(pill);
  }
  if (thread.inferred) {
    const inf = document.createElement("span");
    inf.className = "muted";
    inf.textContent = " (inferred)";
    h2.appendChild(inf);
  }
  if (thread.breadcrumb) {
    const bc = renderBreadcrumb(thread.breadcrumb);
    h2.appendChild(bc);
  }
  header.appendChild(h2);

  // Category-level form controls (Add Item, close/reopen, rename,
  // merge, comment, flag_next_week). Mirrors renderThreadV3's pattern.
  // B9: gate thread_close when the Category still has open Items — gray
  // + warn. The v4 path previously left this to a Worker-only reject, so
  // the button looked clickable ("letting you push through"). Worker
  // stays authoritative; this is the first-line UX.
  const categoryTargetId = threadKey(thread.slug);
  const openItemCount = (thread.work_items ?? []).filter(
    (w) => (w.status ?? "open") === "open",
  ).length;
  header.appendChild(renderFormControls({
    runId,
    targetType: "thread",
    targetId: categoryTargetId,
    kinds: kindsForCategoryV4(thread.status === "closed"),
    submitted: fb.get(categoryTargetId) ?? [],
    participantOptions,
    threadOptions,
    // #38: the add-form mints a collision-free slug against the existing
    // groups (open + closed) + this session's pending adds (the latter ride in
    // via the optimistically-mutated thread.subcategories).
    subcategoryOptions: (thread.subcategories ?? []).map((s) => ({
      slug: s.slug, name: s.name, closed: s.status === "closed",
    })),
    // Pre-fill "Rename Category" with the current name to edit; #37 pre-selects
    // the Category's current phase in the Move-to-Phase select (null => General).
    current: { new_name: thread.name, phase: thread.phase ?? "general" },
    notice: openItemCount > 0 && thread.status !== "closed"
      ? `Close ${openItemCount} open Item${openItemCount === 1 ? "" : "s"} before closing this Category.`
      : undefined,
  }));

  // Category one-liner sits INSIDE the thread-header card so it
  // visually belongs to the Category title rather than floating as
  // a borderless paragraph between Category and first Item. Gated
  // behind CATEGORY_DESCRIPTION_VISIBLE at top of file — the client
  // spec doesn't request Category descriptions.
  if (CATEGORY_DESCRIPTION_VISIBLE && thread.one_liner) {
    const p = document.createElement("p");
    p.className = "thread-one-liner";
    p.textContent = thread.one_liner;
    header.appendChild(p);
  }

  section.appendChild(header);

  // Decision 6B: sort Items within Category by display_number ASC so
  // the path notation reads in order ("3.1 → 3.2 → 3.3"). Matches the
  // Category-level numeric sort in renderBriefingV4. Items with no
  // display_number sort to the end (defensive — pipeline should
  // backfill all numbers).
  const itemCmp = (a: WorkItemView, b: WorkItemView): number => {
    const parseN = (s?: string): number => {
      if (!s) return Number.POSITIVE_INFINITY;
      const parts = s.split(".");
      const last = parts[parts.length - 1];
      const n = parseInt(last ?? "", 10);
      return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
    };
    return parseN(a.display_number) - parseN(b.display_number);
  };
  const openItems = (thread.work_items ?? [])
    .filter((wi) => wi.status !== "closed")
    .sort(itemCmp);
  const closedItems = (thread.work_items ?? [])
    .filter((wi) => wi.status === "closed")
    .sort(itemCmp);

  // #38: sub-category render order — created_at ASC, seq tiebreak (mirrors the
  // agent stamp's sort so optimistic in-session adds slot in at the right spot).
  const scCmp = (a: SubCategoryView, b: SubCategoryView): number => {
    const ca = a.created_at ?? "";
    const cb = b.created_at ?? "";
    if (ca !== cb) return ca < cb ? -1 : 1;
    return (a.seq ?? 0) - (b.seq ?? 0);
  };
  const sortedSubcats = [...(thread.subcategories ?? [])].sort(scCmp);
  // An OPEN Item is "in" a group iff its pointer names an OPEN group under this
  // Category; a null pointer OR a pointer to a closed/missing group (the orphan
  // case) renders in the UNGROUPED prefix — mirroring the stamp's orphan sweep
  // so the SPA never hides an open Item under a closed group's tombstone.
  const openGroupSlugs = new Set(
    sortedSubcats.filter((s) => s.status !== "closed").map((s) => s.slug),
  );
  const groupOfOpen = (wi: WorkItemView): string | null => {
    const p = wi.subcategory_slug;
    return p && openGroupSlugs.has(p) ? p : null;
  };
  // Both open and closed Items carry sortedSubcats through renderItemV4 (the
  // closed-Item "(was in X)" suffix + the open Item's move picker).
  const renderItem = (wi: WorkItemView): HTMLElement => renderItemV4(
    runId, thread.slug, wi, fb, participantOptions, threadOptions,
    itemOptions, allActionOptions, sortedSubcats,
  );

  // UNGROUPED FIRST (legacy-prefix rule): the pre-grouping layout is a strict
  // prefix, so nothing visually jumps when a group is later created.
  for (const wi of openItems.filter((wi) => groupOfOpen(wi) === null)) {
    section.appendChild(renderItem(wi));
  }
  // Then the groups in ONE stamp-ordered sequence (created_at ASC, seq
  // tiebreak): an OPEN group renders its member cards; a CLOSED group renders
  // as a header-only tombstone IN PLACE in the same sequence. Closed groups are
  // interleaved by created_at, NOT bucketed after the open ones — this matches
  // the j2 email's single `for sc in thread.subcategories` loop (briefing-v4.md
  // .j2), so the SPA and the cron render the group order identically.
  for (const sc of sortedSubcats) {
    if (sc.status === "closed") {
      section.appendChild(renderSubcategoryTombstone(runId, thread, sc, fb));
    } else {
      section.appendChild(renderSubcategoryGroup(
        runId, thread, sc,
        openItems.filter((wi) => groupOfOpen(wi) === sc.slug),
        fb, renderItem,
      ));
    }
  }

  // A1: split closed Items into merged (context folded forward into a target)
  // vs plain closed (done/resolved) — a merge is not a close. Closed Items
  // ALWAYS aggregate in the single Category-level disclosure regardless of any
  // group pointer (the 2026-06-07 density fix is preserved), each carrying its
  // "(was in X)" suffix from renderItem.
  const mergedItems = closedItems.filter((wi) => isMergedClosure(wi.closed_reason));
  const plainClosedItems = closedItems.filter((wi) => !isMergedClosure(wi.closed_reason));
  const renderClosedGroup = (items: WorkItemView[], label: string): void => {
    if (!items.length) return;
    // Collapsed by default (2026-06-07 density fix): a big close-out can put
    // dozens of closed Items in the 2-week retention window — tuck them behind
    // a disclosure so they don't bury the active work. Expand for the full
    // closed cards (incl. reopen controls).
    const details = document.createElement("details");
    details.className = "closed-items-group";
    const summary = document.createElement("summary");
    summary.className = "closed-items-divider";
    summary.textContent = `${label} (${items.length})`;
    details.appendChild(summary);
    for (const wi of items) details.appendChild(renderItem(wi));
    section.appendChild(details);
  };
  renderClosedGroup(mergedItems, "Recently Merged Items");
  renderClosedGroup(plainClosedItems, "Recently Closed Items");

  return section;
}

// #38: an OPEN sub-category — a card-less `.subcategory-group` section with an
// unnumbered bold title row (NO path-num span — the unnumbered guarantee), its
// own rename/close/notes controls, and its member Item cards. styles.css
// restores the direct-child card chrome the wrapper would otherwise break
// (the #157-class fix).
function renderSubcategoryGroup(
  runId: string,
  thread: ThreadView,
  sc: SubCategoryView,
  items: WorkItemView[],
  fb: Map<string, SubmittedFeedbackEvent[]>,
  renderItem: (wi: WorkItemView) => HTMLElement,
): HTMLElement {
  const group = document.createElement("section");
  group.className = "subcategory-group";

  const titleRow = document.createElement("div");
  titleRow.className = "subcategory-header";
  const title = document.createElement("h3");
  title.className = "subcategory-title";
  title.textContent = sc.name;
  titleRow.appendChild(title);

  // B9-style first-line gray of the close button when the group still has open
  // member Items; the Worker's effectiveOpenItemsUnderSubcategory stays
  // authoritative. The rendered open count already reflects optimistic closes
  // (a pending-closed member has status="closed" and isn't in `items`).
  const scTargetId = subcategoryKey(thread.slug, sc.slug);
  const openMemberCount = items.filter(
    (w) => (w.status ?? "open") === "open",
  ).length;
  titleRow.appendChild(renderFormControls({
    runId,
    targetType: "subcategory",
    targetId: scTargetId,
    kinds: kindsForSubcategoryV4(false),
    submitted: fb.get(scTargetId) ?? [],
    // Pre-fill "Rename Sub-category" with the current name to edit.
    current: { to_name: sc.name },
    notice: openMemberCount > 0
      ? `Move or close ${openMemberCount} open Item${openMemberCount === 1 ? "" : "s"} in this sub-category before closing it.`
      : undefined,
  }));
  group.appendChild(titleRow);

  for (const wi of items) group.appendChild(renderItem(wi));
  return group;
}

// #38: a CLOSED sub-category renders as a HEADER-ONLY tombstone row inside the
// group sequence for 14 days (struck name + "Closed · DATE" pill + reopen
// pencil), holding no Item cards — the closed members aggregate in the
// Category-level "Recently Closed" disclosure with their "(was in X)" suffix.
// In-place closed rendering (the #158 lesson — verify in ?fixture=2).
function renderSubcategoryTombstone(
  runId: string,
  thread: ThreadView,
  sc: SubCategoryView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
): HTMLElement {
  const row = document.createElement("section");
  row.className = "subcategory-group subcategory-closed";

  const titleRow = document.createElement("div");
  titleRow.className = "subcategory-header";
  const title = document.createElement("h3");
  title.className = "subcategory-title closed";
  title.textContent = sc.name;
  const pill = document.createElement("span");
  pill.className = "status-pill closed";
  pill.textContent = sc.closed_at ? `Closed · ${sc.closed_at}` : "Closed";
  title.appendChild(document.createTextNode(" "));
  title.appendChild(pill);
  titleRow.appendChild(title);

  const scTargetId = subcategoryKey(thread.slug, sc.slug);
  titleRow.appendChild(renderFormControls({
    runId,
    targetType: "subcategory",
    targetId: scTargetId,
    kinds: kindsForSubcategoryV4(true),
    submitted: fb.get(scTargetId) ?? [],
  }));
  row.appendChild(titleRow);
  return row;
}

function renderItemV4(
  runId: string,
  threadSlug: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  itemOptions: ItemOption[],
  allActionOptions: ActionOption[] = [],
  // #38: the parent Category's sub-categories. Feeds (a) the kindsForItemV4
  // ≥1-open-group gate, (b) the move-picker options via renderFormControls,
  // (c) the closed-Item "(was in <group>)" suffix. Default [] keeps every
  // existing call site + the old-blob path byte-identical.
  subcategories: SubCategoryView[] = [],
): HTMLElement {
  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"}`;
  if (wi.status === "closed") wrapper.classList.add("closed");
  // A² "New only" filter marker: an Item is "has-new" when it is itself
  // new OR carries a new Action (so a new Action keeps its Item visible
  // for context under the filter).
  const itemHasNew = !!wi.is_new
    || (wi.action_items ?? []).some((a) => !!a.is_new);
  if (itemHasNew) wrapper.classList.add("has-new");
  // A1 "new/updated" filter marker: an Item is "has-updated" when it was
  // itself MERGE'd this period OR carries an updated Action (mirrors has-new,
  // so a changed Action keeps its Item visible for context under the filter).
  const itemHasUpdated = !!wi.is_updated
    || (wi.action_items ?? []).some((a) => !!a.is_updated);
  if (itemHasUpdated) wrapper.classList.add("has-updated");

  const header = document.createElement("div");
  header.className = "work-item-header";

  const h3 = document.createElement("h3");
  if (wi.display_number) {
    const num = document.createElement("span");
    num.className = "path-num";
    // Demo-reference prefix "Item N.N" matches the v4 design walk
    // (2026-05-22): puts the level-marker word in front of the
    // hierarchical number so operators can scan-read what tier they're
    // looking at without parsing path notation.
    num.textContent = `Item ${wi.display_number}`;
    h3.appendChild(num);
  }
  h3.appendChild(document.createTextNode(wi.name));
  appendNewPill(h3, wi.is_new);
  appendUpdatedPill(h3, wi.is_updated);
  // #58 (PSU = Actions only): no Item-level PSU pill — an Item is never
  // independently marked; it enters the Project Status Update only via a
  // marked Action under it. The Action-level "PSU" pill still renders.
  // Option Y (2026-06-03): Item-level closure proposal. When an open Item's
  // Actions are all complete it carries an Item-level closure_candidate;
  // surface "(Agent proposes closing)" on the title, mirroring the
  // Action-level suffix. The reason is the hover tooltip.
  const itemCandidate = (wi as {
    closure_candidate?: { reason?: string };
  }).closure_candidate;
  if (itemCandidate && wi.status !== "closed") {
    const cc = document.createElement("span");
    cc.className = "item-closure-proposed";
    cc.textContent = " (Agent proposes closing)";
    if (itemCandidate.reason) cc.setAttribute("title", itemCandidate.reason);
    h3.appendChild(cc);
  }
  if (wi.status === "closed") {
    const pill = document.createElement("span");
    const mergedInto = mergeTargetFromReason(wi.closed_reason);
    if (mergedInto) {
      pill.className = "status-pill merged";
      pill.textContent = `Merged → ${mergedInto}`;
    } else {
      pill.className = "status-pill closed";
      pill.textContent = wi.closed_at ? `Closed · ${wi.closed_at}` : "Closed";
    }
    h3.appendChild(document.createTextNode(" "));
    h3.appendChild(pill);
  } else if (wi.status === "acknowledged" || wi.status === "not_relevant") {
    // C5 (2026-07-21): the disposition verbs gray in-session (optimistic echo)
    // like closed does; the next cron then drops them to the Closed Archive's
    // "Acknowledged / Not Tracked" section. No inline date needed.
    const pill = document.createElement("span");
    pill.className = `status-pill ${wi.status === "acknowledged" ? "acknowledged" : "not-relevant"}`;
    pill.textContent = wi.status === "acknowledged" ? "Acknowledged · not tracked" : "Not relevant";
    h3.appendChild(document.createTextNode(" "));
    h3.appendChild(pill);
  }
  // #38: a CLOSED Item that still carries a grouping pointer renders
  // "(was in <group>)" — closed Items aggregate in the Category-level
  // "Recently Closed/Merged" disclosure, not under their group's header, so
  // this suffix is the only place the membership shows. Null-guard: render
  // nothing if the slug doesn't resolve (the stamp windows a group to outlast
  // its members, so a dangling pointer is structurally unlikely).
  if (wi.status === "closed" && wi.subcategory_slug) {
    const grp = subcategories.find((s) => s.slug === wi.subcategory_slug);
    if (grp) {
      const wasIn = document.createElement("span");
      wasIn.className = "muted item-was-in-group";
      wasIn.textContent = ` (was in ${grp.name})`;
      h3.appendChild(wasIn);
    }
  }
  if (wi.breadcrumb) {
    const bc = renderBreadcrumb(wi.breadcrumb);
    h3.appendChild(bc);
  }
  // #66 PR3: the matter-ledger consult's inline annotation. `absorbed` is a
  // muted breadcrumb (a recurrence was folded in this run); `ambiguous` nudges
  // the recipient toward the existing per-Item Merge-Into-Existing affordance
  // (the item menu already offers work_item_merge / item_merge_into). Inline,
  // in-tier — never a separate panel.
  // N1 (2026-07-14): don't surface the merge proposal on a CLOSED/merged Item —
  // once the recipient accepts (or otherwise closes it), the item folds with a
  // "Recently Merged → into X" treatment and the "possibly part of…" nudge is
  // resolved. The optimistic work_item_merge applier sets status="closed" on the
  // source, so this hides the lingering proposal line the moment Accept lands.
  // #66 D1-D3 (2026-07-23): the dedup consult's overlap proposal shares the
  // banner + the Dismiss with the matter note; the dismiss key gains an
  // `:overlap` suffix when an overlap is present so a NEW overlap signal
  // resurfaces past an older matter-only dismissal.
  const overlapPresent = Boolean(wi.merge_candidate) && wi.status !== "closed";
  const proposalDismissKey = mergeBannerDismissKey(
    workItemKey(threadSlug, wi.slug), overlapPresent,
  );
  const resolvedOverlapTargets = overlapPresent
    ? resolveMergeCandidateTargets(wi.merge_candidate, wi.slug, itemOptions)
    : [];
  if (
    wi.matter_note && wi.status !== "closed"
    // operator 2026-07-14: a session Dismiss (in the ✎ menu) clears this inline
    // note too, not just the banner. Item-level key = the item's target id.
    && !mergeProposalDismissed(proposalDismissKey)
  ) {
    // SURFACE-ONLY: the only matter_note kind is `ambiguous` — a dismissable
    // nudge toward the existing per-Item Merge-Into-Existing affordance.
    // Dogfood feedback 1: name the suggestion target(s) by item NUMBER, not
    // just the matter — the recipient sees exactly which Item to merge into.
    // Resolve the candidate member slugs against the project-wide item list;
    // fall back to the matter name if a target isn't rendered this week.
    const mn = document.createElement("span");
    mn.className = "item-matter-note ambiguous";
    const bySlug = new Map(itemOptions.map((o) => [o.slug, o]));
    const targetLabels = (wi.matter_note.targets ?? [])
      .map((s) => bySlug.get(s))
      .filter((o): o is ItemOption => !!o)
      .map((o) => (o.display_number ? `${o.display_number} ${o.name}` : o.name));
    const hasResolvedTarget = targetLabels.length > 0;
    const hint = hasResolvedTarget
      ? `Possibly part of ${targetLabels.join(", ")}`
      : wi.matter_note.text;
    // N1 (2026-07-14): the inline note flags the proposal in the tree; the
    // one-click Accept/Dismiss lives in the ✎ menu's orange proposal banner
    // (see the mergeProposal spec below + showPicker in forms.ts). When no
    // target resolves this week, fall back to the generic "Merge into…" pointer.
    mn.textContent = hasResolvedTarget
      ? ` ◇ ${hint} — open ✎ to accept or dismiss`
      : ` ◇ ${hint} — place via the item menu → “Merge into…”`;
    mn.title = "The agent suggests this Item may belong with a previously-tracked " +
      "matter. Open the ✎ menu to Accept the merge (pre-filled) or Dismiss the " +
      "suggestion and keep it as a distinct facet.";
    h3.appendChild(mn);
  }
  // #66 D1-D3 (2026-07-23): inline overlap note — the synth's own "overlaps
  // with…" cross-reference tell, surfaced as a dismissable consolidation
  // proposal (md-template parity: briefing-v4.md.j2 "◇ … consider
  // consolidating"). When a counterpart Item resolves, the ✎ banner offers
  // one-click merge; otherwise nudge to the existing menu affordances.
  if (
    wi.merge_candidate && wi.status !== "closed"
    && !mergeProposalDismissed(proposalDismissKey)
  ) {
    const on = document.createElement("span");
    on.className = "item-matter-note overlap";
    on.textContent = resolvedOverlapTargets.length
      ? ` ◇ ${wi.merge_candidate.text} — open ✎ to consolidate or dismiss`
      : ` ◇ ${wi.merge_candidate.text} — review via the item menu → “Merge into…”`;
    on.title = "The agent detected this Item may overlap work tracked elsewhere " +
      "(its narrative cross-references it). Open the ✎ menu to merge the two — " +
      "or Dismiss to keep them separate.";
    h3.appendChild(on);
  }
  header.appendChild(h3);

  // Per-item controls strip: chevron compact toggle + pencil. Mirrors
  // the v3 three-state per-item override:
  //   • no override + currently follows Auto/global → chevron points
  //     opposite of current state (action: override to opposite)
  //   • override active → reset-arrow (action: restore Auto/global)
  // Persists override in localStorage like v3 did, scoped by run +
  // work_item slug. effectiveCompactness combines global mode +
  // override + synth's per-item compact hint to compute the
  // rendered state.
  const controlsRow = document.createElement("div");
  controlsRow.className = "work-item-controls-row";

  const globalMode = readCompactnessMode();
  const override = readCompactnessOverride(runId, wi.slug);
  const isCompact = effectiveCompactness(globalMode, override, wi.compact);
  if (isCompact) wrapper.classList.add("work-item-compact");

  const chevronBtn = document.createElement("button");
  chevronBtn.type = "button";
  chevronBtn.className = "btn-edit work-item-toggle-btn";
  if (override !== null) {
    chevronBtn.innerHTML = RESET_ARROW_SVG;
    chevronBtn.title = "Restore Auto";
    chevronBtn.setAttribute("aria-label", "Restore Auto mode");
    chevronBtn.addEventListener("click", () => {
      writeCompactnessOverride(runId, wi.slug, null);
      // Re-render the whole viewer so the chevron state, content
      // visibility, and any related rows resync from current globals.
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
    });
  } else if (isCompact) {
    chevronBtn.innerHTML = CHEVRON_DOWN_SVG;
    chevronBtn.title = "Expand this Item";
    chevronBtn.setAttribute("aria-label", "Expand this Item");
    chevronBtn.addEventListener("click", () => {
      writeCompactnessOverride(runId, wi.slug, "expanded");
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
    });
  } else {
    chevronBtn.innerHTML = CHEVRON_UP_SVG;
    chevronBtn.title = "Compact this Item";
    chevronBtn.setAttribute("aria-label", "Compact this Item");
    chevronBtn.addEventListener("click", () => {
      writeCompactnessOverride(runId, wi.slug, "compact");
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
    });
  }
  controlsRow.appendChild(chevronBtn);

  // Item-level form controls — Add Action, close/reopen, move-to-
  // Category, assign Owner, KIL toggle, comment, flag next week.
  // B9: gate work_item_close when the Item still has open Actions —
  // gray the button + warn, mirroring the Category→Item gate. The
  // Worker is the authoritative enforcer; this is the first-line UX.
  const itemTargetId = workItemKey(threadSlug, wi.slug);
  const openChildActions = (wi.action_items ?? []).filter(
    (a) => (a.status ?? "open") === "open",
  );
  const openActionCount = openChildActions.length;
  // Cascade-close (Ray 2026-07-21): the open children as cascade targets.
  // Offered ONLY when every open child has an ai_slug (an untargetable child
  // would strand the composite mid-way — fall back to the greyed close +
  // notice, the pre-cascade behavior).
  const cascadeOpenActions = openChildActions
    .filter((a) => a.ai_slug)
    .map((a) => ({
      target_id: actionItemKey(threadSlug, wi.slug, a.ai_slug!),
      label: `${a.display_number ? a.display_number + " " : ""}${
        a.text?.trim() || a.what?.trim() || a.ai_slug!}`,
    }));
  const cascadeCloseAvailable = wi.status !== "closed"
    && openActionCount > 0
    && cascadeOpenActions.length === openActionCount;
  // #38: the parent Category's groups for this Item's move picker + the
  // ≥1-open-group gate. `subcategoryOptions` spans open+closed (the add-
  // collision set; the move form filters to open). The picker pre-selects the
  // Item's current group.
  const openGroups = subcategories.filter((s) => s.status !== "closed");
  // N1 (2026-07-14): the agent's "Possibly part of X" matter suggestion, resolved
  // to the open Items rendered this week (a slug not in itemOptions — target not
  // open/rendered, or self — is dropped). Drives the orange Accept/Dismiss proposal
  // banner (showPicker in forms.ts); ≥2 candidates → the banner offers a dropdown.
  const resolvedMatterTargets =
    wi.matter_note?.kind === "ambiguous" && wi.status !== "closed"
      // dedup slugs (the consult can flatten the same open member across two
      // candidate matters) so a lone shared target renders as one option, not a
      // duplicated dropdown row.
      ? [...new Set((wi.matter_note.targets ?? []).filter((slug) => slug && slug !== wi.slug))]
          .map((slug) => {
            const opt = itemOptions.find((o) => o.slug === slug);
            return opt
              ? {
                  slug,
                  label: opt.display_number
                    ? `${opt.display_number} ${opt.name}`
                    : opt.name,
                }
              : null;
          })
          .filter((t): t is { slug: string; label: string } => t !== null)
      : [];
  // #66 D1-D3 (2026-07-23): one merged banner — overlap targets FIRST (the
  // synth's own narrative tell is the stronger, more specific signal than
  // ledger ambiguity), then matter targets, deduped by slug.
  const mergeTargets = [...resolvedOverlapTargets];
  for (const t of resolvedMatterTargets) {
    if (!mergeTargets.some((m) => m.slug === t.slug)) mergeTargets.push(t);
  }
  controlsRow.appendChild(renderFormControls({
    runId,
    targetType: "work_item",
    targetId: itemTargetId,
    kinds: kindsForItemV4(
      wi.status === "closed",
      Boolean((wi as { closure_candidate?: unknown }).closure_candidate),
      openGroups.length > 0,
      // #73: ≥1 OTHER open Item exists to fold this one up into.
      itemOptions.some((o) => o.slug !== wi.slug),
      cascadeCloseAvailable,
    ),
    submitted: fb.get(itemTargetId) ?? [],
    participantOptions,
    threadOptions,
    itemOptions,
    subcategoryOptions: subcategories.map((s) => ({
      slug: s.slug, name: s.name, closed: s.status === "closed",
    })),
    // Pre-fill "Rename Item" with the current name to edit; pre-select the
    // Item's current group on the Move-to-Sub-category picker.
    // R2-3: prefill "Edit narrative" with the Item's current narrative.
    current: {
      to_name: wi.name,
      to_subcategory_slug: wi.subcategory_slug ?? "",
      narrative: wi.narrative ?? "",
      // N1 (2026-07-14): default the Merge-into picker to the FIRST resolved
      // target, so opening the form directly (or the banner Accept with a
      // single candidate) lands on the proposed primary. The banner overrides
      // this with the recipient's dropdown choice when there are ≥2 candidates.
      merge_primary_item: mergeTargets[0]?.slug,
    },
    // N1 (2026-07-14): the agent's merge suggestion(s), surfaced as an orange
    // Accept/Dismiss proposal banner at the TOP of this Item's ✎ menu (showPicker
    // in forms.ts) — mirroring the closure_candidate affordance. Carries EVERY
    // resolved candidate so the banner offers a dropdown (default the first) when
    // more than one resolved. #66 D1-D3: `reason` = the overlap tell rendered as
    // a muted line under the banner lead; `review` gates one-click auto-submit
    // to a visible prefilled form when folding a multi-action Item (whole-Item
    // fold scope guard — the accept merges EVERY action this Item carries).
    mergeProposal: mergeTargets.length
      ? {
          targets: mergeTargets,
          reason: resolvedOverlapTargets.length
            ? wi.merge_candidate?.text
            : undefined,
          review: resolvedOverlapTargets.length > 0 && openActionCount > 1,
          // #66 D4: ONLY the overlap-sourced slugs may be recorded durably.
          // `mergeTargets` is the UNION of overlap + matter targets, so gating
          // on it would let an Item carrying only a matter_note write a
          // permanent dismissal for a pair the dedup judge never proposed.
          durableDismissSlugs: resolvedOverlapTargets.map((t) => t.slug),
        }
      : undefined,
    // #66 D1-D3: overlap-aware dismiss key (shared by the inline notes above).
    mergeProposalDismissKey: proposalDismissKey,
    // A1: label for the optimistic "Recently Merged → into X" treatment.
    mergeTargetLabel: `${wi.display_number ? wi.display_number + " " : ""}${wi.name}`,
    // Decision 3.6: former path captured for the move breadcrumb.
    movingFromDisplayNumber: wi.display_number,
    // B10: a done-but-misfiled Item (0 open Actions) can Move & Close in one
    // step via the Move-to-Category checkbox; gated here.
    itemCloseable: openActionCount === 0,
    // Cascade-close: the open children the composite will close. When the
    // cascade is offered the greyed-close `notice` is dropped (the dead-end
    // it explained no longer exists); the notice remains as the fallback for
    // the rare un-targetable-child case (an open child without an ai_slug).
    cascadeOpenActions: cascadeCloseAvailable ? cascadeOpenActions : undefined,
    notice: !cascadeCloseAvailable && openActionCount > 0 && wi.status !== "closed"
      ? `Close ${openActionCount} open action${openActionCount === 1 ? "" : "s"} before closing this Item.`
      : undefined,
  }));
  header.appendChild(controlsRow);

  // Item summary + responsible + started ride INSIDE the header div
  // (not as siblings after it). This keeps them visually attached to
  // the Item card and lets `.work-item-header > .work-item-summary`
  // CSS govern the spacing rhythm — matches the demo reference where
  // the Item title and its meta sit in the same bordered segment.
  if (wi.narrative) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.narrative;
    header.appendChild(p);
  } else if (wi.summary) {
    // v3 back-compat: payload may carry summary instead of narrative.
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    header.appendChild(p);
  }

  // Item meta line — Responsible + Started joined on one line with
  // `·` separators, matching the Action-meta pattern. Was rendered
  // as separate <p> elements until v0.4.7 — operator caught the
  // regression vs the demo-preview where this is single-line.
  const itemMetaBits: string[] = [];
  if (wi.responsible_party) {
    let rpText = `Responsible: ${wi.responsible_party.name}`;
    if (wi.responsible_party.source === "default_pm") rpText += " (PM default)";
    itemMetaBits.push(rpText);
  }
  const startDate = wi.start_date ?? wi.first_seen;
  if (startDate) {
    itemMetaBits.push(`Started ${startDate}`);
  }
  if (itemMetaBits.length) {
    const meta = document.createElement("p");
    meta.className = "work-item-meta";
    meta.textContent = itemMetaBits.join(" · ");
    header.appendChild(meta);
  }

  wrapper.appendChild(header);

  const openActions = (wi.action_items ?? [])
    .filter((a) => a.status !== "closed");
  const closedActions = (wi.action_items ?? [])
    .filter((a) => a.status === "closed");

  if (openActions.length) {
    // Demo-reference wrapper: each sub-area under an Item (Actions,
    // Open Questions, Recently Closed Actions) gets its own dashed-
    // top-border section with an uppercase label. Adds the missing
    // "ACTIONS" divider that v4 inherited without — operators couldn't
    // tell where the Item meta ended and the action list began.
    const actionsSection = document.createElement("div");
    actionsSection.className = "actions-section";

    const labelRow = document.createElement("div");
    labelRow.className = "actions-section-header";
    const actionsLabel = document.createElement("h4");
    actionsLabel.className = "section-label";
    actionsLabel.textContent = "Actions";
    labelRow.appendChild(actionsLabel);

    // Decision 5.4: "Select all" Item-header bulk shortcut. Visible
    // only when there's at least one unmarked open Action inside the
    // Item. Click fires `action_kil_mark` POSTs in parallel — the
    // Worker mutation makes them appear in the KIL tab instantly,
    // the SPA optimistic listener flips the briefing-row KIL badges
    // in this view. Uses the same `kil-mark-changed` event the
    // single-action mark dispatches, so app.ts's listener handles
    // both code paths uniformly.
    const unmarkedOpenActions = openActions.filter((a) => !a.kil_marked);
    if (BULK_KIL_MARK_VISIBLE && unmarkedOpenActions.length > 0) {
      const bulkBtn = document.createElement("button");
      bulkBtn.type = "button";
      bulkBtn.className = "actions-section-bulk-kil";
      bulkBtn.textContent = `Mark all (${unmarkedOpenActions.length}) for PSU`;
      bulkBtn.title =
        "Mark every unmarked open Action in this Item for the Key " +
        "Issues Log (Decision 5.4 bulk shortcut).";
      bulkBtn.addEventListener("click", async () => {
        bulkBtn.disabled = true;
        const original = bulkBtn.textContent;
        bulkBtn.textContent = `Marking ${unmarkedOpenActions.length}…`;
        try {
          await Promise.all(unmarkedOpenActions.map(async (a) => {
            if (!a.ai_slug) return;
            const targetId = actionItemKey(threadSlug, wi.slug, a.ai_slug);
            await postJson(`/feedback/${encodeURIComponent(runId)}`, {
              kind: "action_kil_mark",
              target: { type: "action_item", id: targetId },
              payload: {},
            });
            document.dispatchEvent(new CustomEvent("kil-mark-changed", {
              detail: { kind: "action_kil_mark", targetId },
            }));
          }));
          // Optimistic mutation already happened via the dispatched
          // events; the listener re-rendered. Button is gone now.
        } catch (e) {
          console.error("bulk KIL mark failed", e);
          bulkBtn.disabled = false;
          bulkBtn.textContent = original;
        }
      });
      labelRow.appendChild(bulkBtn);
    }
    actionsSection.appendChild(labelRow);

    const list = document.createElement("ul");
    list.className = "actions-list";
    // B8 cross-Item action_merge: the merge picker now offers project-wide
    // open Actions (passed down from renderBriefingViewer), not just this
    // Item's siblings. The form excludes the target and labels each option
    // with its parent Item so the recipient sees where it comes from; the
    // submit handler re-parents any cross-Item pick before merging.
    const actionOptions = allActionOptions;
    // Sort by priority enum order — Critical first, On Hold last.
    // Within each priority group, sort by display_number ASC so
    // 3.6.1 renders before 3.6.2.
    for (const priority of PRIORITY_ORDER) {
      const group = openActions
        .filter((a) => ((a.priority_status as PriorityStatus | undefined) ?? "Active") === priority)
        .sort((a, b) => {
          const dn = (s: string | undefined) => {
            if (!s) return 1e9;
            const tail = s.split(".").pop();
            return tail ? parseInt(tail, 10) || 1e9 : 1e9;
          };
          return dn(a.display_number) - dn(b.display_number);
        });
      for (const action of group) {
        list.appendChild(renderActionV4(
          runId, threadSlug, wi.slug, action, fb,
          participantOptions, threadOptions, actionOptions, itemOptions,
        ));
      }
    }
    actionsSection.appendChild(list);
    wrapper.appendChild(actionsSection);
  }

  // Open Questions render INLINE on each Action now (renderActionV4), not in a
  // separate grouped sub-section (2026-06-28 operator: in-tier + actionable).

  // Recently Closed Actions sub-section (Decision 2.2). A1: split out
  // merged Actions ("Recently Merged") from plain closes.
  const mergedActions = closedActions.filter((a) => isMergedClosure(a.closed_reason));
  const plainClosedActions = closedActions.filter((a) => !isMergedClosure(a.closed_reason));
  if (mergedActions.length) {
    wrapper.appendChild(renderRecentlyClosedActions(
      runId, threadSlug, wi.slug, mergedActions, fb,
      participantOptions, threadOptions, "Recently Merged Actions",
    ));
  }
  if (plainClosedActions.length) {
    wrapper.appendChild(renderRecentlyClosedActions(
      runId, threadSlug, wi.slug, plainClosedActions, fb,
      participantOptions, threadOptions, "Recently Closed Actions",
    ));
  }

  return wrapper;
}

function renderActionV4(
  runId: string,
  threadSlug: string,
  workItemSlug: string,
  action: AnyItem,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  actionOptions: ActionOption[] = [],
  itemOptions: ItemOption[] = [],
): HTMLElement {
  const priority = (action.priority_status as PriorityStatus | undefined) ?? "Active";
  const li = document.createElement("li");
  li.className = `action-row ${priorityClass(priority)}`;
  if (action.status === "closed") li.classList.add("closed");
  if (action.is_new) li.classList.add("is-new");

  const head = document.createElement("div");
  head.className = "action-row-head";

  const text = document.createElement("div");
  text.className = "action-text";
  if (action.display_number) {
    const num = document.createElement("span");
    num.className = "path-num";
    num.textContent = action.display_number;
    text.appendChild(num);
  }
  const strong = document.createElement("strong");
  // Defensive against synth emitting an empty `what`/`text`. Falls back
  // to the narrative's first line, then a `(no title)` marker. The
  // agent-side Pydantic `min_length=1` constraint should prevent this
  // from reaching us in normal runs; this is a render-time safety net
  // for pre-constraint state-repo data + future regressions.
  const fallbackTitle = (() => {
    if (action.text && action.text.trim()) return action.text;
    if (action.what && action.what.trim()) return action.what;
    if (action.narrative && action.narrative.trim()) {
      const firstLine = action.narrative.split(/[.\n]/)[0]?.trim();
      if (firstLine) return firstLine;
    }
    return "(no title)";
  })();
  strong.textContent = fallbackTitle;
  text.appendChild(strong);
  appendNewPill(text, action.is_new);
  appendUpdatedPill(text, action.is_updated);

  if ((action.open_questions_v4 ?? []).some((q) => !q.answer)) {
    const q = document.createElement("span");
    q.className = "action-question-badge";
    q.textContent = "❓";
    q.setAttribute("title", "Has an unanswered question (shown below)");
    text.appendChild(q);
  }
  if (action.kil_marked) {
    const kil = document.createElement("span");
    kil.className = "kil-badge-action";
    kil.textContent = "PSU";
    kil.setAttribute("title", "Marked for Project Status Update");
    text.appendChild(kil);
  }
  // Decision 12.8 (2026-05-28): closure_candidate title suffix.
  // Mirrors the (inferred) pattern on Categories — muted span
  // appended to the title with a leading space; hover tooltip
  // shows the synth's grounded reason. Recipient/operator acts via
  // the popover (action_close / action_dismiss_closure_candidate).
  const candidate = (action as { closure_candidate?: { reason?: string } })
    .closure_candidate;
  if (candidate) {
    const cc = document.createElement("span");
    cc.className = "action-closure-proposed";
    cc.textContent = " (Agent proposes closing)";
    if (candidate.reason) cc.setAttribute("title", candidate.reason);
    text.appendChild(cc);
  }
  // Decision 3.6 breadcrumb on a recently moved Action — mirrors the Item
  // header crumb (briefings.ts ~2999) one tier down. The agent sets it on
  // the payload node for 2 weeks after an action_move_to_item.
  if (action.breadcrumb) {
    const bc = renderBreadcrumb(action.breadcrumb);
    text.appendChild(bc);
  }
  // #66 D1-D3 (2026-07-23): cross-source overlap note on an Action — inline
  // advisory only in v1 (md-template parity: "◇ … consider consolidating").
  // Consolidation runs through the existing ✎ menu "Merge into…" / "Move to
  // another Item" affordances; an Action↔Item pair isn't directly mergeable,
  // so no one-click here.
  if (action.merge_candidate && (action.status ?? "open") === "open") {
    const on = document.createElement("span");
    on.className = "action-overlap-note";
    on.textContent = ` ◇ ${action.merge_candidate.text} — consolidate via ✎ ` +
      "“Merge into…” / “Move to another Item”";
    on.title = "The agent detected this Action may overlap work tracked " +
      "elsewhere (its text cross-references it).";
    text.appendChild(on);
  }
  head.appendChild(text);

  const controls = document.createElement("div");
  controls.className = "action-controls";
  const pill = document.createElement("span");
  pill.className = `action-status-pill ${priorityClass(priority)}`;
  if (action.status === "closed") {
    const mergedInto = mergeTargetFromReason(action.closed_reason);
    if (mergedInto) {
      pill.className = "action-status-pill merged";
      pill.textContent = `Merged → ${mergedInto}`;
      pill.title = `Merged → ${mergedInto}`;
    } else {
      pill.className = "action-status-pill closed";
      pill.textContent = action.closed_at
        ? `Closed · ${action.closed_at}`
        : "Closed";
    }
  } else if (action.status === "acknowledged" || action.status === "not_relevant") {
    // C5 (2026-07-21): disposition verbs gray in-session (optimistic echo); the
    // Action drops to the Closed Archive "Acknowledged / Not Tracked" next cron.
    pill.className = `action-status-pill ${action.status === "acknowledged" ? "acknowledged" : "not-relevant"}`;
    pill.textContent = action.status === "acknowledged" ? "Acknowledged" : "Not relevant";
  } else {
    pill.textContent = priority;
  }
  controls.appendChild(pill);

  // Real form controls — picker shows priority change, reason/impact/
  // off-hold setters, due edit, question add/resolve, KIL toggle,
  // and invoice ops on aggregate Actions.
  // Decision 3A: priority-aware filter — pass action.priority_status
  // into kindsForActionV4 so Active doesn't show off_hold_condition
  // etc. Decision 3B: populatedFields tells the form which "Add" vs
  // "Edit" label variant to use.
  if (action.ai_slug) {
    const actionTargetId = actionItemKey(threadSlug, workItemSlug, action.ai_slug);
    const populated = new Set<string>();
    if (action.reason && action.reason.trim()) populated.add("reason");
    if (action.impact && action.impact.trim()) populated.add("impact");
    if (action.off_hold_condition && action.off_hold_condition.trim()) {
      populated.add("off_hold_condition");
    }
    if (action.due && action.due.trim()) populated.add("due");
    const invoiceOptions = action.action_type === "invoices_aggregate"
      ? (action.invoices ?? []).map((inv) => ({
          number: inv.number,
          label: [
            inv.number,
            inv.amount != null ? `$${inv.amount.toFixed(2)}` : null,
            inv.status,
          ].filter(Boolean).join(" · "),
        }))
      : undefined;
    controls.appendChild(renderFormControls({
      runId,
      targetType: "action_item",
      targetId: actionTargetId,
      kinds: kindsForActionV4(
        (action.open_questions_v4 ?? []).length > 0,
        action.action_type === "invoices_aggregate",
        Boolean(action.kil_marked),
        action.priority_status ?? null,
        action.status === "closed" ? "closed" : "open",
        Boolean(
          (action as { closure_candidate?: unknown }).closure_candidate,
        ),
        // B1 / #73: ≥1 OTHER open Action exists project-wide to fold this one
        // up into. actionOptions is the project-wide open-Action set (the same
        // list feeding the forward action_merge picker), self excluded by id.
        actionOptions.some(
          (o) => (o.target_id ?? o.ai_slug) !== actionTargetId,
        ),
      ),
      submitted: fb.get(actionTargetId) ?? [],
      participantOptions,
      threadOptions,
      invoiceOptions,
      actionOptions,
      // #52b: the action_open_question_resolve picker resolves a question by its
      // stable QID (survives the synth's run-to-run re-wording); the value is
      // prefixed `qid:<hex>` (or `text:<text>` for a legacy question without a
      // qid yet), split back apart in reshapePayloadForKind. Label stays the
      // question text. Only unanswered questions are offered.
      openQuestionOptions: (action.open_questions_v4 ?? [])
        .filter((q) => !q.answer)
        .map((q) => ({
          value: q.qid ? `qid:${q.qid}` : `text:${q.text}`,
          label: q.text,
        })),
      // B8: project-wide Item list for the action_move_to_item picker.
      itemOptions,
      priorityStatus: action.priority_status,
      populatedFields: populated,
      // A1: label for the optimistic "Recently Merged → into X" treatment.
      mergeTargetLabel: `${action.display_number ? action.display_number + " " : ""}${action.text ?? action.what ?? ""}`,
      // Decision 3.6: former path captured for the move breadcrumb.
      movingFromDisplayNumber: action.display_number,
      // Pre-fill the edit forms (reason / impact / off-hold / due, and the
      // priority form's metadata) with the Action's current values so the
      // recipient edits what's there rather than starting blank.
      current: {
        reason: action.reason ?? undefined,
        impact: action.impact ?? undefined,
        off_hold_condition: action.off_hold_condition ?? undefined,
        due: action.due ?? undefined,
        // B6: pre-fill "Rename Action" with the current text to edit.
        to_text: action.text ?? action.what ?? undefined,
        // #76: pre-fill "Promote to Item" with the Action text as the new
        // Item's default name (the recipient edits or keeps it).
        name: action.text ?? action.what ?? undefined,
        // R2-3: pre-fill "Edit narrative" with the Action's current narrative.
        narrative: action.narrative ?? "",
      },
    }));
  }
  head.appendChild(controls);

  li.appendChild(head);

  // Narrative + priority metadata + meta line, in order matching
  // the markdown template.
  //
  // Decision 12.6 (2026-05-28, client feedback #2): narrative is a
  // base detail kept visible in both compact and expanded views.
  // Reason / impact / off_hold_condition are "extended detail" —
  // visible only in expanded view. The `.action-extended` class
  // tags them so the compact-mode CSS rule can hide them surgically
  // without affecting the narrative.
  if (action.narrative) {
    const n = document.createElement("p");
    n.className = "action-detail action-narrative";
    n.textContent = action.narrative;
    li.appendChild(n);
  }
  const detailPairs: Array<[string, string]> = [];
  if (action.reason) detailPairs.push(["Reason", action.reason]);
  if (action.impact) detailPairs.push(["Impact", action.impact]);
  if (action.off_hold_condition) {
    detailPairs.push(["Off-hold when", action.off_hold_condition]);
  }
  for (const [label, value] of detailPairs) {
    const p = document.createElement("p");
    p.className = "action-detail action-extended";
    const b = document.createElement("b");
    b.textContent = `${label}:`;
    p.appendChild(b);
    p.appendChild(document.createTextNode(` ${value}`));
    li.appendChild(p);
  }

  // Meta line: Due · Responsible · Started (Decision 2.7). #59: due bit via
  // the shared dueMetaBit helper ("Due date: TBD" when an open Action has none).
  const metaBits: string[] = [];
  const dueBit = dueMetaBit(priority, action.due, action.status);
  if (dueBit) metaBits.push(dueBit);
  const responsibleName = action.responsible_party?.name ?? action.assignee;
  if (responsibleName) metaBits.push(`Responsible: ${responsibleName}`);
  if (action.first_seen) metaBits.push(`Started ${action.first_seen}`);
  if (metaBits.length) {
    const meta = document.createElement("div");
    meta.className = "action-meta";
    meta.textContent = metaBits.join(" · ");
    li.appendChild(meta);
  }

  // Open Questions — rendered INLINE at the BOTTOM of the Action (operator
  // 2026-07-23): the item/action details above (narrative, reason/impact, dates)
  // come FIRST, THEN the question block. A question "is an action" (B5) and the
  // client wants signals in-tier. The resolve control also lives on the ✎ menu
  // (kindsForActionV4 → action_open_question_resolve).
  const questions = action.open_questions_v4 ?? [];
  if (questions.length > 0) {
    // N2/#117 — the Accept button targets this Action; null when it has no
    // ai_slug (then no proposed-answer actions — it can't be resolved).
    const oqActionTargetId = action.ai_slug
      ? actionItemKey(threadSlug, workItemSlug, action.ai_slug)
      : null;
    const qWrap = document.createElement("div");
    qWrap.className = "action-detail action-open-questions";
    for (const q of questions) {
      const row = document.createElement("div");
      row.className = `action-open-question${q.answer ? " answered" : ""}`;

      // Q line — the question with WHO asked it inline (provenance paired to its
      // own line, never orphaned below the answer).
      const qLine = document.createElement("p");
      qLine.className = "oq-line oq-q";
      const qMark = document.createElement("span");
      qMark.className = "oq-mark";
      qMark.textContent = "Q";
      qLine.appendChild(qMark);
      const qBody = document.createElement("span");
      qBody.appendChild(document.createTextNode(q.text));
      const askedBy = askedByMeta(q.raised_by, q.raised_at);
      if (askedBy) {
        const s = document.createElement("span");
        s.className = "oq-inline-meta";
        s.textContent = ` · ${askedBy}`;
        qBody.appendChild(s);
      }
      qLine.appendChild(qBody);

      // Per-question ✎ (client 2026-08-12). Every disposition of THIS question
      // lives here — Answer it, or Dismiss it without answering — rather than on
      // the parent Action's menu behind a "which question" picker. The pencil is
      // the same idiom Categories / Items / Actions already use, one tier down,
      // so nothing new has to be learned.
      //
      // Only for an unanswered question that still has a stable identity: an
      // answered one is already disposed of, and without a qid neither form can
      // name its target (the reconciler mints qids, so this is defensive).
      if (!q.answer && q.qid && oqActionTargetId) {
        const qControls = renderFormControls({
          runId,
          targetType: "action_item",
          targetId: oqActionTargetId,
          kinds: ["action_open_question_resolve", "action_open_question_dismiss"],
          questionContext: { qid: q.qid, text: q.text },
          // Filter to THIS question's events. Every per-question pencil on the
          // Action shares the Action's targetId, so an unfiltered list would
          // cross-show one question's chips and badge count on all of them —
          // the same trap the executive-summary control documents above.
          submitted: (fb.get(oqActionTargetId) ?? []).filter(
            (e) => (e.payload as Record<string, unknown> | undefined)?.qid === q.qid,
          ),
        });
        qControls.classList.add("oq-controls");
        qLine.appendChild(qControls);
      }
      row.appendChild(qLine);

      // Already-answered → the recorded answer (A with a check).
      if (q.answer) {
        const aLine = document.createElement("p");
        aLine.className = "oq-line oq-a";
        const aMark = document.createElement("span");
        aMark.className = "oq-mark oq-check";
        aMark.textContent = "✓";
        aLine.appendChild(aMark);
        const aBody = document.createElement("span");
        aBody.textContent = q.answer;
        aLine.appendChild(aBody);
        row.appendChild(aLine);
      }

      // N2/#117 — a confident, source-grounded PROPOSED answer to an unanswered
      // question. Calm treatment (orange only on the "proposed" tag + actions),
      // with the deterministic source citation, a one-click Accept (submits the
      // existing action_open_question_resolve — kept as a ONE-CLICK shortcut
      // even though the ✎ also offers it, because it is the recipient's most
      // frequent positive action and routing it through a menu would make his
      // commonest interaction slower) and a localStorage-only Hide suggestion.
      const rc = q.resolution_candidate;
      const rcQid = q.qid;
      const dismissKey = rcQid ? `${oqActionTargetId ?? ""}:${rcQid}` : null;
      if (!q.answer && rc && rc.answer && rcQid && oqActionTargetId && dismissKey
          && !proposedAnswerDismissed(dismissKey)) {
        const rcAnswer = rc.answer;
        const rcTarget = oqActionTargetId;
        const dKey = dismissKey;

        const aLine = document.createElement("p");
        aLine.className = "oq-line oq-a";
        const aMark = document.createElement("span");
        aMark.className = "oq-mark";
        aMark.textContent = "A";
        aLine.appendChild(aMark);
        const aBody = document.createElement("span");
        aBody.appendChild(document.createTextNode(`${rcAnswer} `));
        const tag = document.createElement("span");
        tag.className = "oq-proposed-tag";
        tag.textContent = "proposed";
        aBody.appendChild(tag);
        aLine.appendChild(aBody);
        row.appendChild(aLine);

        const srcText = proposedSourceLabel(rc);
        let src: HTMLElement | null = null;
        if (srcText) {
          src = document.createElement("p");
          src.className = "oq-source muted";
          src.textContent = srcText;
          row.appendChild(src);
        }

        const actions = document.createElement("div");
        actions.className = "oq-proposed-actions";
        const accept = document.createElement("button");
        accept.type = "button";
        accept.className = "btn-action proposed-answer-accept";
        accept.textContent = "Accept answer";
        const dismiss = document.createElement("button");
        dismiss.type = "button";
        dismiss.className = "btn-action proposed-answer-dismiss";
        // "Hide suggestion", not "Dismiss": this is a localStorage-only,
        // per-device hide of the PROPOSAL — the question stays open and
        // tracked. Since 2026-08-12 the question row also carries a ✎ whose
        // "Dismiss question" writes a real correction, and two controls a few
        // pixels apart both reading "dismiss" would be genuinely ambiguous.
        dismiss.textContent = "Hide suggestion";
        const help = document.createElement("span");
        help.className = "oq-proposed-help muted";
        help.textContent =
          "Accepting records it as the answer; hiding just removes the "
          + "suggestion on this device — the question stays open.";

        accept.addEventListener("click", async () => {
          accept.disabled = true;
          dismiss.disabled = true;
          accept.textContent = "Accepting…";
          try {
            await acceptProposedAnswer(runId, rcTarget, rcQid, rcAnswer);
            // No re-render on submit — turn the row INTO an answered question so
            // the effect is visible (the answer folds into the narrative next
            // update #87; the standalone Q drops next run #101).
            row.classList.add("answered");
            aMark.className = "oq-mark oq-check";
            aMark.textContent = "✓";
            tag.remove();
            const note = document.createElement("span");
            note.className = "oq-inline-meta";
            note.textContent = " · you accepted, folds into the next update";
            aBody.appendChild(note);
            src?.remove();
            actions.remove();
          } catch {
            accept.disabled = false;
            dismiss.disabled = false;
            accept.textContent = "Accept answer";
            help.textContent = "Couldn't submit — please try again.";
          }
        });
        dismiss.addEventListener("click", () => {
          // localStorage-only (mirrors the N1 merge dismiss): hide the suggestion
          // for this recipient; the candidate stays in state.
          dismissProposedAnswer(dKey);
          aLine.remove();
          src?.remove();
          actions.remove();
        });
        actions.appendChild(accept);
        actions.appendChild(dismiss);
        actions.appendChild(help);
        row.appendChild(actions);
      }

      qWrap.appendChild(row);
    }
    li.appendChild(qWrap);
  }

  // Billing aggregate: nested invoices list (Section 11.4).
  if (action.action_type === "invoices_aggregate" && (action.invoices ?? []).length) {
    const invList = document.createElement("ul");
    invList.className = "action-invoices-list";
    for (const inv of action.invoices ?? []) {
      const row = document.createElement("li");
      const num = document.createElement("b");
      num.textContent = inv.number;
      row.appendChild(num);
      const tail: string[] = [];
      if (inv.amount != null) tail.push(`$${inv.amount.toFixed(2)}`);
      if (inv.status) tail.push(`(${inv.status})`);
      if (inv.due_date) tail.push(`due ${inv.due_date}`);
      if (inv.blocking_note) tail.push(inv.blocking_note);
      if (tail.length) {
        row.appendChild(document.createTextNode(` — ${tail.join(" · ")}`));
      }
      invList.appendChild(row);
    }
    li.appendChild(invList);
  }

  return li;
}

function renderRecentlyClosedActions(
  runId: string,
  threadSlug: string,
  workItemSlug: string,
  closedActions: AnyItem[],
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  labelText = "Recently Closed Actions",
): HTMLElement {
  // Collapsed by default (2026-06-07 density fix): after a big close-out the
  // 2-week window can hold dozens of closed Actions per Item — tuck them
  // behind a disclosure so they don't dominate the Item card.
  const section = document.createElement("details");
  section.className = "recently-closed-section";

  const label = document.createElement("summary");
  label.className = "section-label";
  label.textContent = `${labelText} (${closedActions.length})`;
  section.appendChild(label);

  const ul = document.createElement("ul");
  ul.className = "actions-list";
  for (const action of closedActions) {
    ul.appendChild(renderActionV4(
      runId, threadSlug, workItemSlug, action, fb,
      participantOptions, threadOptions,
    ));
  }
  section.appendChild(ul);
  return section;
}


function renderNeedsReviewV4(
  _runId: string,
  reviewItems: NonNullable<BriefingPayload["needs_review"]>,
  _fb: Map<string, SubmittedFeedbackEvent[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread thread-v4 needs-review";
  const h2 = document.createElement("h2");
  h2.textContent = "Needs Review";
  section.appendChild(h2);
  const ul = document.createElement("ul");
  for (const r of reviewItems) {
    const li = document.createElement("li");
    const strong = document.createElement("strong");
    strong.textContent = r.summary;
    li.appendChild(strong);
    if (r.why_flagged) {
      li.appendChild(document.createTextNode(` — ${r.why_flagged}`));
    }
    const m = document.createElement("span");
    m.className = "muted";
    m.textContent = ` (item ${r.item_id})`;
    li.appendChild(m);
    ul.appendChild(li);
  }
  section.appendChild(ul);
  return section;
}
